import React from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Calendar } from 'primereact/calendar';
import { InputTextarea } from 'primereact/inputtextarea';
import { AutoComplete } from 'primereact/autocomplete';
import acordLogo from 'images/acordlogo.png';
import CreateNewCarriers from '../CreateNewCarriers';
import AddNewSignature from '../AddNewSignature';
import { useSignatures } from '../../../../hooks/useSignatures';
import ProducerSection, { fetchFirstPolicyAgentContact, fetchAgentContact } from '../ProducerSection';

import { formatUsPhone } from 'utilities/formatPhone';
import { resolveCarrierIdByName, resolveCarrierById, lookupCarrierIdSync } from '../InsurerSearchDropdown';
import InsurerNaicField from '../InsurerNaicField';
import { createMachineOwned } from '../../utils/machineOwned';
import { mapFields, getInsurers, getAllRemarkLines, getPolicyAddresses, pickPolicyAddress } from '../acordFieldEngine';
import type { AcordFieldItem } from '../acordFieldEngine';
import { PROPERTY_MAP, INLAND_MAP, CRIME_MAP, BOILER_MAP, OTHER24_MAP } from './acord24FieldMap';
import type { Acord24FormData } from '../../utils/acord24Payload';
import { fetchBuildingCoverages } from '../../api/masterCertificateApi';
import { useSelectedClient } from '../../../../hooks/useSelectedClient';
import { parseAcord24StoredCoverages } from '../../utils/parseAcord24StoredCoverages';
import type { StoredCoverage, StoredPolicy } from '../../utils/parseAcord24StoredCoverages';

interface Acord24FormProps {
  showAcord101?: boolean;
  isV2016?: boolean;
  selectedPolicies?: string[];
  /** "Additional Named Insured(s): ..." text derived from selected policies' customFields — auto-fills the ACORD 101 remarks textarea */
  acord101AdditionalNamedInsureds?: string;
  insuredId?: string;
  selectedLocationLabel?: string;
  editCoverages?: StoredCoverage[];
  editPolicies?: StoredPolicy[];
  onLoadingChange?: (loading: boolean) => void;
}

export interface Acord24FormRef {
  getFormData(): Acord24FormData;
  applyBuildingCoverages(returnObjects: Array<{ fieldName: string; value: string }>): void;
}

const INSR_OPTIONS = ['A','B','C','D','E','F'].map(l => ({ label: l, value: l }));

const Acord24Form = React.forwardRef<Acord24FormRef, Acord24FormProps>(
function Acord24Form({ showAcord101 = false, isV2016 = false, selectedPolicies = [], acord101AdditionalNamedInsureds = '', insuredId = '', selectedLocationLabel = '', editCoverages, editPolicies, onLoadingChange }, ref) {
  // Tracker #241: explain the blank cell while no policy is selected. Display only; never saved.
  const noPolicyPlaceholder = selectedPolicies.length === 0 ? 'Select a policy above or type a number' : undefined;
  const { client } = useSelectedClient();
  const [showCreateCarrier, setShowCreateCarrier] = React.useState(false);
  const [showAddSignature, setShowAddSignature] = React.useState(false);
  const [producerAddress, setProducerAddress] = React.useState('');
  const [producerCity, setProducerCity] = React.useState('');
  const [producerState, setProducerState] = React.useState('');
  const [producerZip, setProducerZip] = React.useState('');
  const [producerStreet, setProducerStreet] = React.useState('');
  const [contactName, setContactName] = React.useState('');
  const [contactPhone, setContactPhone] = React.useState('');
  const [contactFax, setContactFax] = React.useState('');
  const [contactEmail, setContactEmail] = React.useState('');
  const [producerCustomerId, setProducerCustomerId] = React.useState('');
  const [insuredName, setInsuredName] = React.useState('');
  const [insuredAddress, setInsuredAddress] = React.useState('');
  const [insuredStreet, setInsuredStreet] = React.useState('');
  const [insuredCity, setInsuredCity] = React.useState('');
  const [insuredState, setInsuredState] = React.useState('');
  const [insuredZip, setInsuredZip] = React.useState('');

  // Insured block: filled from the client only while a policy is selected (Farmers, 9/24) — a
  // certificate with no policy is created blank, not pre-filled. A selected policy's address
  // then overrides these through get-coverages; any policy counts, even one that fills no
  // coverage row. Edit mode: insured comes from stored XML, not the live client.
  React.useEffect(() => {
    if (!client) return;
    if (isEditMode.current) { setAcord101NamedInsured(client.name || ''); return; }
    if (!selectedPolicies || selectedPolicies.length === 0) {
      setAcord101NamedInsured('');
      setInsuredName('');
      setInsuredAddress('');
      setInsuredStreet('');
      setInsuredCity('');
      setInsuredState('');
      setInsuredZip('');
      return;
    }
    setAcord101NamedInsured(client.name || '');
    setInsuredName(client.name || '');
    setInsuredAddress(client.addressLine1 || '');
    setInsuredStreet(client.addressLine2 || '');
    setInsuredCity(client.city || '');
    setInsuredState(client.state || '');
    setInsuredZip(client.zip || '');
  }, [client, selectedPolicies]);
  const [insurerA, setInsurerA] = React.useState('');
  const [insurerB, setInsurerB] = React.useState('');
  const [insurerC, setInsurerC] = React.useState('');
  const [insurerD, setInsurerD] = React.useState('');
  const [insurerE, setInsurerE] = React.useState('');
  const [insurerF, setInsurerF] = React.useState('');
  const [insurerAId, setInsurerAId] = React.useState<string | null>(null);
  const [insurerBId, setInsurerBId] = React.useState<string | null>(null);
  const [insurerCId, setInsurerCId] = React.useState<string | null>(null);
  const [insurerDId, setInsurerDId] = React.useState<string | null>(null);
  const [insurerEId, setInsurerEId] = React.useState<string | null>(null);
  const [insurerFId, setInsurerFId] = React.useState<string | null>(null);
  // Tracker #257: agent-typed cells survive a policy change unless new policy data competes.
  const { own } = React.useRef(createMachineOwned()).current;
  const [insurerANaic, setInsurerANaic] = React.useState('');
  const [insurerBNaic, setInsurerBNaic] = React.useState('');
  const [insurerCNaic, setInsurerCNaic] = React.useState('');
  const [insurerDNaic, setInsurerDNaic] = React.useState('');
  const [insurerENaic, setInsurerENaic] = React.useState('');
  const [insurerFNaic, setInsurerFNaic] = React.useState('');
  const [certDate, setCertDate] = React.useState<Date | null>(new Date());
  const [certNumber, setCertNumber] = React.useState('');
  const [revisionNumber, setRevisionNumber] = React.useState('');
  const [locationProperty, setLocationProperty] = React.useState('');
  const [selectedPropertyId, setSelectedPropertyId] = React.useState('');
  const [descriptionOfProperty, setDescriptionOfProperty] = React.useState('');

  // Properties API — fetches all properties for the dropdown; also used for policy-filtered prefill
  const [propertyOptions, setPropertyOptions] = React.useState<{ label: string; value: string; address: string; policyIds: string[] }[]>([]);
  React.useEffect(() => {
    if (!insuredId || !selectedPolicies?.length) {
      setPropertyOptions([]);
      return;
    }
    const ncToken = localStorage.getItem('NCToken') || '';
    const igToken = localStorage.getItem('igtoken') || '';
    fetch(
      `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/insured-properties?top=100&insuredDatabaseId=${encodeURIComponent(insuredId)}&policyIds=${encodeURIComponent(selectedPolicies.join(','))}`,
      { headers: { Authorization: `Bearer ${igToken}`, NCToken: ncToken, 'Content-Type': 'application/json' } }
    )
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        const items = Array.isArray(data?.value) ? data.value : [];
        setPropertyOptions(items.map((p: any) => {
          const parts = [p.addressLine1, p.addressLine2, p.city, p.state, p.zip].filter(Boolean);
          const label = parts.join(', ') || p.description || p.databaseId;
          const address = parts.join('\n');
          return { label, value: p.databaseId, address, policyIds: Array.isArray(p.policyIds) ? p.policyIds : [] };
        }));
      })
      .catch(() => setPropertyOptions([]));
  }, [insuredId, selectedPolicies]);

  // Prefill description from the selected location in the General section
  React.useEffect(() => {
    if (editPrefillPending.current) return;
    setDescriptionOfProperty(selectedLocationLabel);
  }, [selectedLocationLabel]);

  // Auto-prefill "Location of Premises / Description of Property" from policies' linked properties.
  // Backend filters properties by policyIds — we only receive matching properties.
  // Tracks last auto-filled value so policy changes update the field without clobbering manual edits.
  const lastAutoPrefillLocationRef = React.useRef('');
  React.useEffect(() => {
    if (!insuredId) return;
    if (editPrefillPending.current) return;
    if (!selectedPolicies?.length) {
      setDescriptionOfProperty(prev => prev === lastAutoPrefillLocationRef.current ? '' : prev);
      lastAutoPrefillLocationRef.current = '';
      return;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPolicies, insuredId]);

  const [propInsurerLtr, setPropInsurerLtr] = React.useState('');
  const [propChecked, setPropChecked] = React.useState(false);
  const [propPolicyNumber, setPropPolicyNumber] = React.useState('');
  const [propEffDate, setPropEffDate] = React.useState<Date | null>(null);
  const [propExpDate, setPropExpDate] = React.useState<Date | null>(null);
  const [propBasic, setPropBasic] = React.useState(false);
  const [propBroad, setPropBroad] = React.useState(false);
  const [propSpecial, setPropSpecial] = React.useState(false);
  const [propEarthquake, setPropEarthquake] = React.useState(false);
  const [propWind, setPropWind] = React.useState(false);
  const [propFlood, setPropFlood] = React.useState(false);
  const [propDeductBuilding, setPropDeductBuilding] = React.useState('');
  const [propDeductContents, setPropDeductContents] = React.useState('');
  const [propEarthquakeDeduct, setPropEarthquakeDeduct] = React.useState('');
  const [propWindDeduct, setPropWindDeduct] = React.useState('');
  const [propFloodDeduct, setPropFloodDeduct] = React.useState('');
  const [propCovBuilding, setPropCovBuilding] = React.useState(false);
  const [propLimBuilding, setPropLimBuilding] = React.useState('');
  const [propCovPersonalProp, setPropCovPersonalProp] = React.useState(false);
  const [propLimPersonalProp, setPropLimPersonalProp] = React.useState('');
  const [propCovBusinessIncome, setPropCovBusinessIncome] = React.useState(false);
  const [propLimBusinessIncome, setPropLimBusinessIncome] = React.useState('');
  const [propCovExtraExpense, setPropCovExtraExpense] = React.useState(false);
  const [propLimExtraExpense, setPropLimExtraExpense] = React.useState('');
  const [propCovRentalValue, setPropCovRentalValue] = React.useState(false);
  const [propLimRentalValue, setPropLimRentalValue] = React.useState('');
  const [propCovBlanketBuilding, setPropCovBlanketBuilding] = React.useState(false);
  const [propLimBlanketBuilding, setPropLimBlanketBuilding] = React.useState('');
  const [propCovBlanketPersProp, setPropCovBlanketPersProp] = React.useState(false);
  const [propLimBlanketPersProp, setPropLimBlanketPersProp] = React.useState('');
  const [propCovBlanketBldgPP, setPropCovBlanketBldgPP] = React.useState(false);
  const [propLimBlanketBldgPP, setPropLimBlanketBldgPP] = React.useState('');

  // Property — "other" cause-of-loss rows
  const [propOther1, setPropOther1] = React.useState(false);
  const [propOther1Text, setPropOther1Text] = React.useState('');
  const [propOther1Deduct, setPropOther1Deduct] = React.useState('');
  const [propOther2, setPropOther2] = React.useState(false);
  const [propOther2Text, setPropOther2Text] = React.useState('');
  const [propOther2Deduct, setPropOther2Deduct] = React.useState('');
  // Property — extra "other" covered-property rows
  const [propOtherCov1, setPropOtherCov1] = React.useState(false);
  const [propOtherCov1Desc, setPropOtherCov1Desc] = React.useState('');
  const [propOtherCov1Lim, setPropOtherCov1Lim] = React.useState('');
  const [propOtherCov2, setPropOtherCov2] = React.useState(false);
  const [propOtherCov2Desc, setPropOtherCov2Desc] = React.useState('');
  const [propOtherCov2Lim, setPropOtherCov2Lim] = React.useState('');

  // Inland Marine
  const [inlandInsurerLtr, setInlandInsurerLtr] = React.useState('');
  const [inlandChecked, setInlandChecked] = React.useState(false);
  const [inlandNamedPerils, setInlandNamedPerils] = React.useState(false);
  const [inlandOtherPerils, setInlandOtherPerils] = React.useState(false);
  const [inlandOtherPerilsText, setInlandOtherPerilsText] = React.useState('');
  const [inlandTypeOfPolicy, setInlandTypeOfPolicy] = React.useState('');
  const [inlandPolicyNumber, setInlandPolicyNumber] = React.useState('');
  const [inlandEffDate, setInlandEffDate] = React.useState<Date | null>(null);
  const [inlandExpDate, setInlandExpDate] = React.useState<Date | null>(null);
  // Coverage rows A–D (each has indicator + description + limit)
  const [inlandCov1, setInlandCov1] = React.useState(false);
  const [inlandCov1Desc, setInlandCov1Desc] = React.useState('');
  const [inlandCov1Lim, setInlandCov1Lim] = React.useState('');
  const [inlandCov2, setInlandCov2] = React.useState(false);
  const [inlandCov2Desc, setInlandCov2Desc] = React.useState('');
  const [inlandCov2Lim, setInlandCov2Lim] = React.useState('');
  const [inlandCov3, setInlandCov3] = React.useState(false);
  const [inlandCov3Desc, setInlandCov3Desc] = React.useState('');
  const [inlandCov3Lim, setInlandCov3Lim] = React.useState('');
  const [inlandCov4, setInlandCov4] = React.useState(false);
  const [inlandCov4Desc, setInlandCov4Desc] = React.useState('');
  const [inlandCov4Lim, setInlandCov4Lim] = React.useState('');

  // Crime
  const [crimeInsurerLtr, setCrimeInsurerLtr] = React.useState('');
  const [crimeChecked, setCrimeChecked] = React.useState(false);
  const [crimeTypeOfPolicy, setCrimeTypeOfPolicy] = React.useState('');
  const [crimeCausesOfLoss, setCrimeCausesOfLoss] = React.useState('');
  const [crimePolicyNumber, setCrimePolicyNumber] = React.useState('');
  const [crimeEffDate, setCrimeEffDate] = React.useState<Date | null>(null);
  const [crimeExpDate, setCrimeExpDate] = React.useState<Date | null>(null);
  // Coverage rows A–C
  const [crimeCov1, setCrimeCov1] = React.useState(false);
  const [crimeCov1Desc, setCrimeCov1Desc] = React.useState('');
  const [crimeCov1Lim, setCrimeCov1Lim] = React.useState('');
  const [crimeCov2, setCrimeCov2] = React.useState(false);
  const [crimeCov2Desc, setCrimeCov2Desc] = React.useState('');
  const [crimeCov2Lim, setCrimeCov2Lim] = React.useState('');
  const [crimeCov3, setCrimeCov3] = React.useState(false);
  const [crimeCov3Desc, setCrimeCov3Desc] = React.useState('');
  const [crimeCov3Lim, setCrimeCov3Lim] = React.useState('');

  // Boiler & Machinery — coverage rows A–B
  const [boilerInsurerLtr, setBoilerInsurerLtr] = React.useState('');
  const [boilerChecked, setBoilerChecked] = React.useState(false);
  const [boilerPolicyNumber, setBoilerPolicyNumber] = React.useState('');
  const [boilerEffDate, setBoilerEffDate] = React.useState<Date | null>(null);
  const [boilerExpDate, setBoilerExpDate] = React.useState<Date | null>(null);
  const [boilerCov1, setBoilerCov1] = React.useState(false);
  const [boilerCov1Desc, setBoilerCov1Desc] = React.useState('');
  const [boilerCov1Lim, setBoilerCov1Lim] = React.useState('');
  const [boilerCov2, setBoilerCov2] = React.useState(false);
  const [boilerCov2Desc, setBoilerCov2Desc] = React.useState('');
  const [boilerCov2Lim, setBoilerCov2Lim] = React.useState('');

  // Other — insurer letter + coverage rows
  const [otherInsurerLtr, setOtherInsurerLtr] = React.useState('');
  const [otherType, setOtherType] = React.useState('');
  const [otherPolicyNumber, setOtherPolicyNumber] = React.useState('');
  const [otherEffDate, setOtherEffDate] = React.useState<Date | null>(null);
  const [otherExpDate, setOtherExpDate] = React.useState<Date | null>(null);
  const [otherCov1, setOtherCov1] = React.useState(false);
  const [otherCov1Desc, setOtherCov1Desc] = React.useState('');
  const [otherCov1Lim, setOtherCov1Lim] = React.useState('');
  const [otherCov2, setOtherCov2] = React.useState(false);
  const [otherCov2Desc, setOtherCov2Desc] = React.useState('');
  const [otherCov2Lim, setOtherCov2Lim] = React.useState('');

  const [specialConditions, setSpecialConditions] = React.useState('');

  const [holderName, setHolderName] = React.useState('');
  const [holderAddress, setHolderAddress] = React.useState('');
  const [holderCity, setHolderCity] = React.useState('');
  const [holderStreet, setHolderStreet] = React.useState('');
  const [holderState, setHolderState] = React.useState('');
  const [holderZip, setHolderZip] = React.useState('');
  const [holderSearchQuery, setHolderSearchQuery] = React.useState('');
  const [holderSuggestions, setHolderSuggestions] = React.useState<{ label: string; value: any }[]>([]);

  const fetchHolderSuggestions = React.useCallback(async (query: string) => {
    if (query.length <= 1) return;
    try {
      const NCToken = localStorage.getItem('NCToken');
      const igtoken = localStorage.getItem('igtoken');
      const response = await fetch(
        `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/certificate-holders?top=10&search=${encodeURIComponent(query)}`,
        { headers: { 'NCToken': NCToken || '', 'Authorization': `Bearer ${igtoken || ''}` } }
      );
      if (!response.ok) return;
      const data = await response.json();
      const ncUserId = localStorage.getItem('NCUserId');
      const all: { label: string; value: any }[] = (data.value || []).map((item: any) => ({ label: item.name, value: item }));
      const filtered = all.filter((item) => {
        const aiDetails: any[] = (item.value?.additionalInsuredDetails ?? [])
          .filter((ai: any) => ai.truckingCompanyId === insuredId);
        if (aiDetails.length === 0) return item.value?.lastChangeUserId === ncUserId;
        if (selectedPolicies.length > 0) {
          return aiDetails.some((ai: any) => {
            const aiPolicyIds: string[] = (ai.policies ?? []).map((p: any) => p.databaseId as string);
            return aiPolicyIds.some(pid => selectedPolicies.includes(pid));
          });
        }
        return true;
      });
      setHolderSuggestions(filtered);
    } catch {
      // non-critical
    }
  }, [insuredId, selectedPolicies]);
  const [holderEnabled, setHolderEnabled] = React.useState(false);
  const [authRepSignature, setAuthRepSignature] = React.useState('');
  const { signatureOptions, refetchSignatures } = useSignatures();
  const [signaturePreviewUrl, setSignaturePreviewUrl] = React.useState('');
  React.useEffect(() => {
    if (!authRepSignature) { setSignaturePreviewUrl(''); return; }
    fetch(`${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/signature-preview/${authRepSignature}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('igtoken') || ''}`,
        'Content-Type': 'application/json',
        NCToken: localStorage.getItem('NCToken') || '',
      },
    })
      .then(r => r.ok ? r.text() : Promise.reject())
      .then(url => setSignaturePreviewUrl(url.replace(/^"|"$/g, '')))
      .catch(() => setSignaturePreviewUrl(''));
  }, [authRepSignature]);

  const [acord101Agency, setAcord101Agency] = React.useState('');
  const [acord101NamedInsured, setAcord101NamedInsured] = React.useState('');
  const [acord101AdditionalRemarks, setAcord101AdditionalRemarks] = React.useState('');

  const editPrefillPending = React.useRef(!!(editCoverages && editCoverages.length > 0));
  // Stable flag set once at mount — never changes, unlike editPrefillPending above.
  const isEditMode = React.useRef(!!(editCoverages && editCoverages.length > 0));
  // A cert saved with no linked policies still has stored coverage rows (blank numbers and
  // limits), but the "restore on first policy change" trigger never fires for it. Track that
  // so the coverage effect can restore it immediately instead (see there).
  const editHasLinkedPolicies = React.useRef(!!(editPolicies && editPolicies.length > 0));

  // ── Building coverage prefill (from location/property selection) ──────────
  const prefillPropertyFromBuildingCoverages = (returnObjects: Array<{ fieldName: string; value: string }>) => {
    const get = (name: string) => returnObjects.find(o => o.fieldName === name)?.value ?? '';
    const getBool = (name: string) => get(name) === 'Yes';

    setPropChecked(getBool('Policy_PolicyType_PropertyIndicator_A'));
    setPropBasic(getBool('Policy_PolicyType_BasicIndicator_A'));
    setPropBroad(getBool('Policy_PolicyType_BroadIndicator_A'));
    setPropSpecial(getBool('Policy_PolicyType_SpecialIndicator_A'));
    setPropEarthquake(getBool('CommercialPropertyCoverage_EarthquakeOption_IncludedIndicator_A'));
    setPropWind(getBool('Policy_PolicyType_WindIndicator_A'));
    setPropFlood(getBool('CommercialPropertyCoverage_Flood_YesIndicator_A'));
    setPropOther1(getBool('Policy_PolicyType_OtherIndicator_A'));
    setPropOther1Text(get('Policy_PolicyType_OtherDescription_A'));
    setPropOther1Deduct(get('CommercialProperty_Premises_DeductibleAmount_F'));
    setPropOther2(getBool('Policy_PolicyType_OtherIndicator_B'));
    setPropOther2Text(get('Policy_PolicyType_OtherDescription_B'));
    setPropOther2Deduct(get('CommercialProperty_Premises_DeductibleAmount_G'));
    setPropDeductBuilding(get('CommercialProperty_Premises_DeductibleAmount_A'));
    setPropDeductContents(get('CommercialProperty_Premises_DeductibleAmount_B'));
    setPropEarthquakeDeduct(get('CommercialProperty_Premises_DeductibleAmount_C'));
    setPropWindDeduct(get('CommercialProperty_Premises_DeductibleAmount_D'));
    setPropFloodDeduct(get('CommercialProperty_Premises_DeductibleAmount_E'));
    setPropCovBuilding(getBool('Property_Building_CoverageIndicator_A'));
    setPropLimBuilding(get('Property_Building_LimitAmount_A'));
    setPropCovPersonalProp(getBool('Property_PersonalProperty_CoverageIndicator_A'));
    setPropLimPersonalProp(get('Property_PersonalProperty_LimitAmount_A'));
    setPropCovBusinessIncome(getBool('CommercialPropertyCoverage_BusinessIncomeOption_IncludedIndicator_A'));
    setPropLimBusinessIncome(get('CommercialPropertyCoverage_BusinessIncome_LimitAmount_A'));
    setPropCovExtraExpense(getBool('CommercialPropertyCoverage_ExtraExpenseOption_IncludedIndicator_A'));
    setPropLimExtraExpense(get('CommercialPropertyCoverage_ExtraExpense_LimitAmount_A'));
    setPropCovRentalValue(getBool('CommercialPropertyCoverage_RentalValueOption_IncludedIndicator_A'));
    setPropLimRentalValue(get('CommercialPropertyCoverage_RentalValue_LimitAmount_A'));
    setPropCovBlanketBuilding(getBool('CommercialProperty_Blanket_YesIndicator_A'));
    setPropLimBlanketBuilding(get('CommercialProperty_Blanket_LimitAmount_A'));
    setPropCovBlanketPersProp(getBool('CommercialProperty_Blanket_YesIndicator_B'));
    setPropLimBlanketPersProp(get('CommercialProperty_Blanket_LimitAmount_B'));
    setPropCovBlanketBldgPP(getBool('CommercialProperty_Blanket_YesIndicator_C'));
    setPropLimBlanketBldgPP(get('CommercialProperty_Blanket_LimitAmount_C'));
    setPropOtherCov1(getBool('PropertyCoverage_OtherOption_IncludedIndicator_A'));
    setPropOtherCov1Desc(get('PropertyCoverage_Other_CoverageDescription_A'));
    setPropOtherCov1Lim(get('PropertyCoverage_Other_LimitAmount_A'));
    setPropOtherCov2(getBool('PropertyCoverage_OtherOption_IncludedIndicator_B'));
    setPropOtherCov2Desc(get('PropertyCoverage_Other_CoverageDescription_B'));
    setPropOtherCov2Lim(get('PropertyCoverage_Other_LimitAmount_B'));
    const policyNumber = get('Policy_Property_PolicyNumberIdentifier_A');
    const effDateStr = get('Policy_Property_EffectiveDate_A');
    const expDateStr = get('Policy_Property_ExpirationDate_A');
    if (policyNumber) setPropPolicyNumber(policyNumber);
    if (effDateStr) setPropEffDate(new Date(effDateStr));
    if (expDateStr) setPropExpDate(new Date(expDateStr));
  };

  // ── Coverage prefill ───────────────────────────────────────────────────────
  const clearCoverages = () => {
    setInsurerA(own('InsurerA', '')); setInsurerB(own('InsurerB', '')); setInsurerC(own('InsurerC', ''));
    setInsurerD(own('InsurerD', '')); setInsurerE(own('InsurerE', '')); setInsurerF(own('InsurerF', ''));
    setInsurerANaic(own('InsurerANaic', '')); setInsurerBNaic(own('InsurerBNaic', '')); setInsurerCNaic(own('InsurerCNaic', ''));
    setInsurerDNaic(own('InsurerDNaic', '')); setInsurerENaic(own('InsurerENaic', '')); setInsurerFNaic(own('InsurerFNaic', ''));
    setInsurerAId(own('InsurerAId', null)); setInsurerBId(own('InsurerBId', null)); setInsurerCId(own('InsurerCId', null));
    setInsurerDId(own('InsurerDId', null)); setInsurerEId(own('InsurerEId', null)); setInsurerFId(own('InsurerFId', null));
    // Property
    setPropInsurerLtr(own('PropInsurerLtr', '')); setPropChecked(own('PropChecked', false));
    setPropPolicyNumber(own('PropPolicyNumber', '')); setPropEffDate(own('PropEffDate', null)); setPropExpDate(own('PropExpDate', null));
    setPropBasic(own('PropBasic', false)); setPropBroad(own('PropBroad', false)); setPropSpecial(own('PropSpecial', false));
    setPropEarthquake(own('PropEarthquake', false)); setPropWind(own('PropWind', false)); setPropFlood(own('PropFlood', false));
    setPropDeductBuilding(own('PropDeductBuilding', '')); setPropDeductContents(own('PropDeductContents', ''));
    setPropEarthquakeDeduct(own('PropEarthquakeDeduct', '')); setPropWindDeduct(own('PropWindDeduct', '')); setPropFloodDeduct(own('PropFloodDeduct', ''));
    setPropOther1(own('PropOther1', false)); setPropOther1Text(own('PropOther1Text', '')); setPropOther1Deduct(own('PropOther1Deduct', ''));
    setPropOther2(own('PropOther2', false)); setPropOther2Text(own('PropOther2Text', '')); setPropOther2Deduct(own('PropOther2Deduct', ''));
    setPropCovBuilding(own('PropCovBuilding', false)); setPropLimBuilding(own('PropLimBuilding', ''));
    setPropCovPersonalProp(own('PropCovPersonalProp', false)); setPropLimPersonalProp(own('PropLimPersonalProp', ''));
    setPropCovBusinessIncome(own('PropCovBusinessIncome', false)); setPropLimBusinessIncome(own('PropLimBusinessIncome', ''));
    setPropCovExtraExpense(own('PropCovExtraExpense', false)); setPropLimExtraExpense(own('PropLimExtraExpense', ''));
    setPropCovRentalValue(own('PropCovRentalValue', false)); setPropLimRentalValue(own('PropLimRentalValue', ''));
    setPropCovBlanketBuilding(own('PropCovBlanketBuilding', false)); setPropLimBlanketBuilding(own('PropLimBlanketBuilding', ''));
    setPropCovBlanketPersProp(own('PropCovBlanketPersProp', false)); setPropLimBlanketPersProp(own('PropLimBlanketPersProp', ''));
    setPropCovBlanketBldgPP(own('PropCovBlanketBldgPP', false)); setPropLimBlanketBldgPP(own('PropLimBlanketBldgPP', ''));
    setPropOtherCov1(own('PropOtherCov1', false)); setPropOtherCov1Desc(own('PropOtherCov1Desc', '')); setPropOtherCov1Lim(own('PropOtherCov1Lim', ''));
    setPropOtherCov2(own('PropOtherCov2', false)); setPropOtherCov2Desc(own('PropOtherCov2Desc', '')); setPropOtherCov2Lim(own('PropOtherCov2Lim', ''));
    // Inland Marine
    setInlandInsurerLtr(own('InlandInsurerLtr', '')); setInlandChecked(own('InlandChecked', false));
    setInlandNamedPerils(own('InlandNamedPerils', false)); setInlandOtherPerils(own('InlandOtherPerils', false)); setInlandOtherPerilsText(own('InlandOtherPerilsText', ''));
    setInlandTypeOfPolicy(own('InlandTypeOfPolicy', '')); setInlandPolicyNumber(own('InlandPolicyNumber', ''));
    setInlandEffDate(own('InlandEffDate', null)); setInlandExpDate(own('InlandExpDate', null));
    setInlandCov1(own('InlandCov1', false)); setInlandCov1Desc(own('InlandCov1Desc', '')); setInlandCov1Lim(own('InlandCov1Lim', ''));
    setInlandCov2(own('InlandCov2', false)); setInlandCov2Desc(own('InlandCov2Desc', '')); setInlandCov2Lim(own('InlandCov2Lim', ''));
    setInlandCov3(own('InlandCov3', false)); setInlandCov3Desc(own('InlandCov3Desc', '')); setInlandCov3Lim(own('InlandCov3Lim', ''));
    setInlandCov4(own('InlandCov4', false)); setInlandCov4Desc(own('InlandCov4Desc', '')); setInlandCov4Lim(own('InlandCov4Lim', ''));
    // Crime
    setCrimeInsurerLtr(own('CrimeInsurerLtr', '')); setCrimeChecked(own('CrimeChecked', false));
    setCrimeTypeOfPolicy(own('CrimeTypeOfPolicy', '')); setCrimeCausesOfLoss(own('CrimeCausesOfLoss', ''));
    setCrimePolicyNumber(own('CrimePolicyNumber', '')); setCrimeEffDate(own('CrimeEffDate', null)); setCrimeExpDate(own('CrimeExpDate', null));
    setCrimeCov1(own('CrimeCov1', false)); setCrimeCov1Desc(own('CrimeCov1Desc', '')); setCrimeCov1Lim(own('CrimeCov1Lim', ''));
    setCrimeCov2(own('CrimeCov2', false)); setCrimeCov2Desc(own('CrimeCov2Desc', '')); setCrimeCov2Lim(own('CrimeCov2Lim', ''));
    setCrimeCov3(own('CrimeCov3', false)); setCrimeCov3Desc(own('CrimeCov3Desc', '')); setCrimeCov3Lim(own('CrimeCov3Lim', ''));
    // Boiler
    setBoilerInsurerLtr(own('BoilerInsurerLtr', '')); setBoilerChecked(own('BoilerChecked', false));
    setBoilerPolicyNumber(own('BoilerPolicyNumber', '')); setBoilerEffDate(own('BoilerEffDate', null)); setBoilerExpDate(own('BoilerExpDate', null));
    setBoilerCov1(own('BoilerCov1', false)); setBoilerCov1Desc(own('BoilerCov1Desc', '')); setBoilerCov1Lim(own('BoilerCov1Lim', ''));
    setBoilerCov2(own('BoilerCov2', false)); setBoilerCov2Desc(own('BoilerCov2Desc', '')); setBoilerCov2Lim(own('BoilerCov2Lim', ''));
    // Other
    setOtherInsurerLtr(own('OtherInsurerLtr', '')); setOtherType(own('OtherType', '')); setOtherPolicyNumber(own('OtherPolicyNumber', ''));
    setOtherEffDate(own('OtherEffDate', null)); setOtherExpDate(own('OtherExpDate', null));
    setOtherCov1(own('OtherCov1', false)); setOtherCov1Desc(own('OtherCov1Desc', '')); setOtherCov1Lim(own('OtherCov1Lim', ''));
    setOtherCov2(own('OtherCov2', false)); setOtherCov2Desc(own('OtherCov2Desc', '')); setOtherCov2Lim(own('OtherCov2Lim', ''));
    setSpecialConditions(own('SpecialConditions', ''));
    setSelectedPropertyId(own('SelectedPropertyId', ''));
  };

  const prefillFromApi = (data: AcordFieldItem[]) => {
    const ins = getInsurers(data);
    setInsurerA(own('InsurerA', ins.A.name)); setInsurerB(own('InsurerB', ins.B.name)); setInsurerC(own('InsurerC', ins.C.name));
    setInsurerD(own('InsurerD', ins.D.name)); setInsurerE(own('InsurerE', ins.E.name)); setInsurerF(own('InsurerF', ins.F.name));
    setInsurerANaic(own('InsurerANaic', ins.A.naic)); setInsurerBNaic(own('InsurerBNaic', ins.B.naic)); setInsurerCNaic(own('InsurerCNaic', ins.C.naic));
    setInsurerDNaic(own('InsurerDNaic', ins.D.naic)); setInsurerENaic(own('InsurerENaic', ins.E.naic)); setInsurerFNaic(own('InsurerFNaic', ins.F.naic));
    // Resolve carrier database IDs by name so NaicId is populated on create
    const apiSlots: Array<[string, React.Dispatch<React.SetStateAction<string | null>>]> = [
      [ins.A.name, setInsurerAId], [ins.B.name, setInsurerBId],
      [ins.C.name, setInsurerCId], [ins.D.name, setInsurerDId],
      [ins.E.name, setInsurerEId], [ins.F.name, setInsurerFId],
    ];
    apiSlots.forEach(([name, setter], i) => {
      if (name) resolveCarrierIdByName(name).then(id => { if (id) setter(own(`Insurer${'ABCDEF'[i]}Id`, id)); });
    });

    const prop = mapFields(data, PROPERTY_MAP);
    if (prop.policyNumber) setPropPolicyNumber(own('PropPolicyNumber', prop.policyNumber));
    if (prop.effDate)      setPropEffDate(own('PropEffDate', prop.effDate));
    if (prop.expDate)      setPropExpDate(own('PropExpDate', prop.expDate));
    if (prop.insurerLtr)   setPropInsurerLtr(own('PropInsurerLtr', prop.insurerLtr));

    if (!isV2016 && !showAcord101) {
      setPropChecked(own('PropChecked', prop.checked));
      setPropBasic(own('PropBasic', prop.basic));       setPropBroad(own('PropBroad', prop.broad));         setPropSpecial(own('PropSpecial', prop.special));
      setPropEarthquake(own('PropEarthquake', prop.earthquake)); setPropWind(own('PropWind', prop.wind));      setPropFlood(own('PropFlood', prop.flood));
      setPropOther1(own('PropOther1', prop.other1)); setPropOther1Text(own('PropOther1Text', prop.other1Text)); setPropOther1Deduct(own('PropOther1Deduct', prop.other1Deduct));
      setPropOther2(own('PropOther2', prop.other2)); setPropOther2Text(own('PropOther2Text', prop.other2Text)); setPropOther2Deduct(own('PropOther2Deduct', prop.other2Deduct));
      setPropDeductBuilding(own('PropDeductBuilding', prop.deductBuilding)); setPropDeductContents(own('PropDeductContents', prop.deductContents));
      setPropEarthquakeDeduct(own('PropEarthquakeDeduct', prop.earthquakeDeduct)); setPropWindDeduct(own('PropWindDeduct', prop.windDeduct)); setPropFloodDeduct(own('PropFloodDeduct', prop.floodDeduct));
      setPropCovBuilding(own('PropCovBuilding', prop.covBuilding));       setPropLimBuilding(own('PropLimBuilding', prop.limBuilding));
      setPropCovPersonalProp(own('PropCovPersonalProp', prop.covPersonalProp)); setPropLimPersonalProp(own('PropLimPersonalProp', prop.limPersonalProp));
      setPropCovBusinessIncome(own('PropCovBusinessIncome', prop.covBusinessIncome)); setPropLimBusinessIncome(own('PropLimBusinessIncome', prop.limBusinessIncome));
      setPropCovExtraExpense(own('PropCovExtraExpense', prop.covExtraExpense));     setPropLimExtraExpense(own('PropLimExtraExpense', prop.limExtraExpense));
      setPropCovRentalValue(own('PropCovRentalValue', prop.covRentalValue));       setPropLimRentalValue(own('PropLimRentalValue', prop.limRentalValue));
      setPropCovBlanketBuilding(own('PropCovBlanketBuilding', prop.covBlanketBuilding)); setPropLimBlanketBuilding(own('PropLimBlanketBuilding', prop.limBlanketBuilding));
      setPropCovBlanketPersProp(own('PropCovBlanketPersProp', prop.covBlanketPersProp)); setPropLimBlanketPersProp(own('PropLimBlanketPersProp', prop.limBlanketPersProp));
      setPropCovBlanketBldgPP(own('PropCovBlanketBldgPP', prop.covBlanketBldgPP));     setPropLimBlanketBldgPP(own('PropLimBlanketBldgPP', prop.limBlanketBldgPP));
      setPropOtherCov1(own('PropOtherCov1', prop.otherCov1)); setPropOtherCov1Desc(own('PropOtherCov1Desc', prop.otherCov1Desc)); setPropOtherCov1Lim(own('PropOtherCov1Lim', prop.otherCov1Lim));
      setPropOtherCov2(own('PropOtherCov2', prop.otherCov2)); setPropOtherCov2Desc(own('PropOtherCov2Desc', prop.otherCov2Desc)); setPropOtherCov2Lim(own('PropOtherCov2Lim', prop.otherCov2Lim));
    }

    const inland = mapFields(data, INLAND_MAP);
    if (inland.insurerLtr) setInlandInsurerLtr(own('InlandInsurerLtr', inland.insurerLtr));
    setInlandChecked(own('InlandChecked', inland.checked));
    setInlandNamedPerils(own('InlandNamedPerils', inland.namedPerils));
    setInlandOtherPerils(own('InlandOtherPerils', inland.otherPerils)); setInlandOtherPerilsText(own('InlandOtherPerilsText', inland.otherPerilsText));
    setInlandTypeOfPolicy(own('InlandTypeOfPolicy', inland.typeOfPolicy));
    setInlandPolicyNumber(own('InlandPolicyNumber', inland.policyNumber));
    setInlandEffDate(own('InlandEffDate', inland.effDate)); setInlandExpDate(own('InlandExpDate', inland.expDate));
    setInlandCov1(own('InlandCov1', inland.cov1)); setInlandCov1Desc(own('InlandCov1Desc', inland.cov1Desc)); setInlandCov1Lim(own('InlandCov1Lim', inland.cov1Lim));
    setInlandCov2(own('InlandCov2', inland.cov2)); setInlandCov2Desc(own('InlandCov2Desc', inland.cov2Desc)); setInlandCov2Lim(own('InlandCov2Lim', inland.cov2Lim));
    setInlandCov3(own('InlandCov3', inland.cov3)); setInlandCov3Desc(own('InlandCov3Desc', inland.cov3Desc)); setInlandCov3Lim(own('InlandCov3Lim', inland.cov3Lim));
    setInlandCov4(own('InlandCov4', inland.cov4)); setInlandCov4Desc(own('InlandCov4Desc', inland.cov4Desc)); setInlandCov4Lim(own('InlandCov4Lim', inland.cov4Lim));

    const crime = mapFields(data, CRIME_MAP);
    if (crime.insurerLtr) setCrimeInsurerLtr(own('CrimeInsurerLtr', crime.insurerLtr));
    setCrimeChecked(own('CrimeChecked', crime.checked));
    setCrimeTypeOfPolicy(own('CrimeTypeOfPolicy', crime.typeOfPolicy));
    setCrimePolicyNumber(own('CrimePolicyNumber', crime.policyNumber));
    setCrimeEffDate(own('CrimeEffDate', crime.effDate)); setCrimeExpDate(own('CrimeExpDate', crime.expDate));
    setCrimeCov1(own('CrimeCov1', crime.cov1)); setCrimeCov1Desc(own('CrimeCov1Desc', crime.cov1Desc)); setCrimeCov1Lim(own('CrimeCov1Lim', crime.cov1Lim));
    setCrimeCov2(own('CrimeCov2', crime.cov2)); setCrimeCov2Desc(own('CrimeCov2Desc', crime.cov2Desc)); setCrimeCov2Lim(own('CrimeCov2Lim', crime.cov2Lim));
    setCrimeCov3(own('CrimeCov3', crime.cov3)); setCrimeCov3Desc(own('CrimeCov3Desc', crime.cov3Desc)); setCrimeCov3Lim(own('CrimeCov3Lim', crime.cov3Lim));

    const boiler = mapFields(data, BOILER_MAP);
    if (boiler.insurerLtr) setBoilerInsurerLtr(own('BoilerInsurerLtr', boiler.insurerLtr));
    setBoilerChecked(own('BoilerChecked', boiler.checked));
    setBoilerPolicyNumber(own('BoilerPolicyNumber', boiler.policyNumber));
    setBoilerEffDate(own('BoilerEffDate', boiler.effDate)); setBoilerExpDate(own('BoilerExpDate', boiler.expDate));
    setBoilerCov1(own('BoilerCov1', boiler.cov1)); setBoilerCov1Desc(own('BoilerCov1Desc', boiler.cov1Desc)); setBoilerCov1Lim(own('BoilerCov1Lim', boiler.cov1Lim));
    setBoilerCov2(own('BoilerCov2', boiler.cov2)); setBoilerCov2Desc(own('BoilerCov2Desc', boiler.cov2Desc)); setBoilerCov2Lim(own('BoilerCov2Lim', boiler.cov2Lim));

    const other = mapFields(data, OTHER24_MAP);
    if (other.insurerLtr) setOtherInsurerLtr(own('OtherInsurerLtr', other.insurerLtr));
    setOtherType(own('OtherType', other.type));
    setOtherPolicyNumber(own('OtherPolicyNumber', other.policyNumber));
    setOtherEffDate(own('OtherEffDate', other.effDate)); setOtherExpDate(own('OtherExpDate', other.expDate));
    setOtherCov1(own('OtherCov1', other.cov1)); setOtherCov1Desc(own('OtherCov1Desc', other.cov1Desc)); setOtherCov1Lim(own('OtherCov1Lim', other.cov1Lim));
    setOtherCov2(own('OtherCov2', other.cov2)); setOtherCov2Desc(own('OtherCov2Desc', other.cov2Desc)); setOtherCov2Lim(own('OtherCov2Lim', other.cov2Lim));

    setSpecialConditions(own('SpecialConditions', getAllRemarkLines(data, 'CertificateOfLiabilityInsurance_ACORDForm_RemarkText_B')));
  };

  React.useEffect(() => {
    clearCoverages();
    const noPolicySelected = !selectedPolicies || selectedPolicies.length === 0;

    // Edit mode: on the first policy change, restore the stored coverage XML instead of
    // calling get-coverages (which returns policy defaults, not saved values). A cert saved
    // with no linked policies never gets that first change, so restore it right away; if we
    // waited, the pending flag would survive and the agent's first policy pick would restore
    // the blank stored XML instead of fetching live coverages (Farmers report 9/21, policy
    // 605490300, tracker #241).
    if (editPrefillPending.current && (!noPolicySelected || !editHasLinkedPolicies.current)) {
      editPrefillPending.current = false;
      if (editCoverages && editCoverages.length > 0) {
        const p = parseAcord24StoredCoverages(editCoverages, editPolicies || []);
        if (p.insuredName)    setInsuredName(own('InsuredName', p.insuredName));
        if (p.insuredAddress) setInsuredAddress(own('InsuredAddress', p.insuredAddress));
        if (p.insuredStreet)  setInsuredStreet(own('InsuredStreet', p.insuredStreet));
        if (p.insuredCity)    setInsuredCity(own('InsuredCity', p.insuredCity));
        if (p.insuredState)   setInsuredState(own('InsuredState', p.insuredState));
        if (p.insuredZip)     setInsuredZip(own('InsuredZip', p.insuredZip));
        setInsurerA(own('InsurerA', p.insurerA)); setInsurerB(own('InsurerB', p.insurerB)); setInsurerC(own('InsurerC', p.insurerC));
        setInsurerD(own('InsurerD', p.insurerD)); setInsurerE(own('InsurerE', p.insurerE)); setInsurerF(own('InsurerF', p.insurerF));
        setInsurerAId(own('InsurerAId', p.insurerAId ?? null)); setInsurerBId(own('InsurerBId', p.insurerBId ?? null)); setInsurerCId(own('InsurerCId', p.insurerCId ?? null));
        setInsurerDId(own('InsurerDId', p.insurerDId ?? null)); setInsurerEId(own('InsurerEId', p.insurerEId ?? null)); setInsurerFId(own('InsurerFId', p.insurerFId ?? null));
        // For old certs with null stored naiCid, resolve IDs by carrier name from the dropdown cache
        const slotNames: Array<[string, React.Dispatch<React.SetStateAction<string | null>>]> = [
          [p.insurerA, setInsurerAId], [p.insurerB, setInsurerBId],
          [p.insurerC, setInsurerCId], [p.insurerD, setInsurerDId],
          [p.insurerE, setInsurerEId], [p.insurerF, setInsurerFId],
        ];
        const idsFromPrefill = [p.insurerAId, p.insurerBId, p.insurerCId, p.insurerDId, p.insurerEId, p.insurerFId];
        const naicSetters = [setInsurerANaic, setInsurerBNaic, setInsurerCNaic, setInsurerDNaic, setInsurerENaic, setInsurerFNaic];
        // The NAIC # is not stored on the certificate; it comes from the carrier record.
        const fillNaicFromId = (id: string, i: number) => {
          resolveCarrierById(id).then(o => { if (o?.naic) naicSetters[i](own(`Insurer${'ABCDEF'[i]}Naic`, o.naic)); });
        };
        slotNames.forEach(([name, setter], i) => {
          const storedId = idsFromPrefill[i];
          if (storedId) {
            fillNaicFromId(storedId, i);
          } else if (name) {
            resolveCarrierIdByName(name).then(id => { if (id) { setter(own(`Insurer${'ABCDEF'[i]}Id`, id)); fillNaicFromId(id, i); } });
          }
        });
        if (p.certDate)       setCertDate(own('CertDate', p.certDate));
        if (p.certNumber)     setCertNumber(own('CertNumber', p.certNumber));
        if (p.revisionNumber) setRevisionNumber(own('RevisionNumber', p.revisionNumber));
        setLocationProperty(own('LocationProperty', p.locationProperty || selectedLocationLabel));
        setDescriptionOfProperty(own('DescriptionOfProperty', p.descriptionOfProperty || p.locationProperty || selectedLocationLabel));
        setSpecialConditions(own('SpecialConditions', p.specialConditions));
        if (p.contactName)      setContactName(own('ContactName', p.contactName));
        if (p.contactPhone)     setContactPhone(own('ContactPhone', p.contactPhone));
        if (p.contactFax)       setContactFax(own('ContactFax', p.contactFax));
        if (p.contactEmail)     setContactEmail(own('ContactEmail', p.contactEmail));
        if (p.producerCustomerId) setProducerCustomerId(own('ProducerCustomerId', p.producerCustomerId));
        // Section-enable flags
        setPropChecked(own('PropChecked', p.propChecked));
        setInlandChecked(own('InlandChecked', p.inlandChecked));
        setCrimeChecked(own('CrimeChecked', p.crimeChecked));
        setBoilerChecked(own('BoilerChecked', p.boilerChecked));
        // Property
        setPropInsurerLtr(own('PropInsurerLtr', p.propInsurerLtr)); setPropPolicyNumber(own('PropPolicyNumber', p.propPolicyNumber));
        setPropEffDate(own('PropEffDate', p.propEffDate)); setPropExpDate(own('PropExpDate', p.propExpDate));
        setPropBasic(own('PropBasic', p.propBasic)); setPropBroad(own('PropBroad', p.propBroad)); setPropSpecial(own('PropSpecial', p.propSpecial));
        setPropEarthquake(own('PropEarthquake', p.propEarthquake)); setPropWind(own('PropWind', p.propWind)); setPropFlood(own('PropFlood', p.propFlood));
        setPropDeductBuilding(own('PropDeductBuilding', p.propDeductBuilding)); setPropDeductContents(own('PropDeductContents', p.propDeductContents));
        setPropEarthquakeDeduct(own('PropEarthquakeDeduct', p.propEarthquakeDeduct)); setPropWindDeduct(own('PropWindDeduct', p.propWindDeduct));
        setPropFloodDeduct(own('PropFloodDeduct', p.propFloodDeduct));
        setPropOther1(own('PropOther1', p.propOther1)); setPropOther1Text(own('PropOther1Text', p.propOther1Text)); setPropOther1Deduct(own('PropOther1Deduct', p.propOther1Deduct));
        setPropOther2(own('PropOther2', p.propOther2)); setPropOther2Text(own('PropOther2Text', p.propOther2Text)); setPropOther2Deduct(own('PropOther2Deduct', p.propOther2Deduct));
        setPropCovBuilding(own('PropCovBuilding', p.propCovBuilding)); setPropLimBuilding(own('PropLimBuilding', p.propLimBuilding));
        setPropCovPersonalProp(own('PropCovPersonalProp', p.propCovPersonalProp)); setPropLimPersonalProp(own('PropLimPersonalProp', p.propLimPersonalProp));
        setPropCovBusinessIncome(own('PropCovBusinessIncome', p.propCovBusinessIncome)); setPropLimBusinessIncome(own('PropLimBusinessIncome', p.propLimBusinessIncome));
        setPropCovExtraExpense(own('PropCovExtraExpense', p.propCovExtraExpense)); setPropLimExtraExpense(own('PropLimExtraExpense', p.propLimExtraExpense));
        setPropCovRentalValue(own('PropCovRentalValue', p.propCovRentalValue)); setPropLimRentalValue(own('PropLimRentalValue', p.propLimRentalValue));
        setPropCovBlanketBuilding(own('PropCovBlanketBuilding', p.propCovBlanketBuilding)); setPropLimBlanketBuilding(own('PropLimBlanketBuilding', p.propLimBlanketBuilding));
        setPropCovBlanketPersProp(own('PropCovBlanketPersProp', p.propCovBlanketPersProp)); setPropLimBlanketPersProp(own('PropLimBlanketPersProp', p.propLimBlanketPersProp));
        setPropCovBlanketBldgPP(own('PropCovBlanketBldgPP', p.propCovBlanketBldgPP)); setPropLimBlanketBldgPP(own('PropLimBlanketBldgPP', p.propLimBlanketBldgPP));
        setPropOtherCov1(own('PropOtherCov1', p.propOtherCov1)); setPropOtherCov1Desc(own('PropOtherCov1Desc', p.propOtherCov1Desc)); setPropOtherCov1Lim(own('PropOtherCov1Lim', p.propOtherCov1Lim));
        setPropOtherCov2(own('PropOtherCov2', p.propOtherCov2)); setPropOtherCov2Desc(own('PropOtherCov2Desc', p.propOtherCov2Desc)); setPropOtherCov2Lim(own('PropOtherCov2Lim', p.propOtherCov2Lim));
        // Inland
        setInlandInsurerLtr(own('InlandInsurerLtr', p.inlandInsurerLtr)); setInlandPolicyNumber(own('InlandPolicyNumber', p.inlandPolicyNumber));
        setInlandEffDate(own('InlandEffDate', p.inlandEffDate)); setInlandExpDate(own('InlandExpDate', p.inlandExpDate));
        setInlandNamedPerils(own('InlandNamedPerils', p.inlandNamedPerils)); setInlandOtherPerils(own('InlandOtherPerils', p.inlandOtherPerils));
        setInlandOtherPerilsText(own('InlandOtherPerilsText', p.inlandOtherPerilsText)); setInlandTypeOfPolicy(own('InlandTypeOfPolicy', p.inlandTypeOfPolicy));
        setInlandCov1(own('InlandCov1', p.inlandCov1)); setInlandCov1Desc(own('InlandCov1Desc', p.inlandCov1Desc)); setInlandCov1Lim(own('InlandCov1Lim', p.inlandCov1Lim));
        setInlandCov2(own('InlandCov2', p.inlandCov2)); setInlandCov2Desc(own('InlandCov2Desc', p.inlandCov2Desc)); setInlandCov2Lim(own('InlandCov2Lim', p.inlandCov2Lim));
        setInlandCov3(own('InlandCov3', p.inlandCov3)); setInlandCov3Desc(own('InlandCov3Desc', p.inlandCov3Desc)); setInlandCov3Lim(own('InlandCov3Lim', p.inlandCov3Lim));
        setInlandCov4(own('InlandCov4', p.inlandCov4)); setInlandCov4Desc(own('InlandCov4Desc', p.inlandCov4Desc)); setInlandCov4Lim(own('InlandCov4Lim', p.inlandCov4Lim));
        // Crime
        setCrimeInsurerLtr(own('CrimeInsurerLtr', p.crimeInsurerLtr)); setCrimePolicyNumber(own('CrimePolicyNumber', p.crimePolicyNumber));
        setCrimeEffDate(own('CrimeEffDate', p.crimeEffDate)); setCrimeExpDate(own('CrimeExpDate', p.crimeExpDate));
        setCrimeTypeOfPolicy(own('CrimeTypeOfPolicy', p.crimeTypeOfPolicy));
        setCrimeCov1(own('CrimeCov1', p.crimeCov1)); setCrimeCov1Desc(own('CrimeCov1Desc', p.crimeCov1Desc)); setCrimeCov1Lim(own('CrimeCov1Lim', p.crimeCov1Lim));
        setCrimeCov2(own('CrimeCov2', p.crimeCov2)); setCrimeCov2Desc(own('CrimeCov2Desc', p.crimeCov2Desc)); setCrimeCov2Lim(own('CrimeCov2Lim', p.crimeCov2Lim));
        setCrimeCov3(own('CrimeCov3', p.crimeCov3)); setCrimeCov3Desc(own('CrimeCov3Desc', p.crimeCov3Desc)); setCrimeCov3Lim(own('CrimeCov3Lim', p.crimeCov3Lim));
        // Boiler
        setBoilerInsurerLtr(own('BoilerInsurerLtr', p.boilerInsurerLtr)); setBoilerPolicyNumber(own('BoilerPolicyNumber', p.boilerPolicyNumber));
        setBoilerEffDate(own('BoilerEffDate', p.boilerEffDate)); setBoilerExpDate(own('BoilerExpDate', p.boilerExpDate));
        setBoilerCov1(own('BoilerCov1', p.boilerCov1)); setBoilerCov1Desc(own('BoilerCov1Desc', p.boilerCov1Desc)); setBoilerCov1Lim(own('BoilerCov1Lim', p.boilerCov1Lim));
        setBoilerCov2(own('BoilerCov2', p.boilerCov2)); setBoilerCov2Desc(own('BoilerCov2Desc', p.boilerCov2Desc)); setBoilerCov2Lim(own('BoilerCov2Lim', p.boilerCov2Lim));
        // Other
        setOtherInsurerLtr(own('OtherInsurerLtr', p.otherInsurerLtr)); setOtherType(own('OtherType', p.otherType));
        setOtherPolicyNumber(own('OtherPolicyNumber', p.otherPolicyNumber));
        setOtherEffDate(own('OtherEffDate', p.otherEffDate)); setOtherExpDate(own('OtherExpDate', p.otherExpDate));
        setOtherCov1(own('OtherCov1', p.otherCov1)); setOtherCov1Desc(own('OtherCov1Desc', p.otherCov1Desc)); setOtherCov1Lim(own('OtherCov1Lim', p.otherCov1Lim));
        setOtherCov2(own('OtherCov2', p.otherCov2)); setOtherCov2Desc(own('OtherCov2Desc', p.otherCov2Desc)); setOtherCov2Lim(own('OtherCov2Lim', p.otherCov2Lim));
        if (p.acord101AdditionalRemarks) setAcord101AdditionalRemarks(own('Acord101AdditionalRemarks', p.acord101AdditionalRemarks));
        if (p.authRepSignature) setAuthRepSignature(own('AuthRepSignature', p.authRepSignature));
        if (p.holderName) {
          setHolderEnabled(own('HolderEnabled', true));
          setHolderName(own('HolderName', p.holderName));
          setHolderAddress(own('HolderAddress', p.holderAddress));
          setHolderCity(own('HolderCity', p.holderCity));
          setHolderStreet(own('HolderStreet', p.holderStreet));
          setHolderState(own('HolderState', p.holderState));
          setHolderZip(own('HolderZip', p.holderZip));
        }
      }
      onLoadingChange?.(false);
      return;
    }

    if (noPolicySelected) return;

    let cancelled = false;
    const fetchCoverages = async () => {
      onLoadingChange?.(true);
      try {
        const ncToken = localStorage.getItem('NCToken') || '';
        const igToken = localStorage.getItem('igtoken') || '';
        const res = await fetch(
          `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/get-coverages`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'NCToken': ncToken,
              Authorization: `Bearer ${igToken}`,
            },
            body: JSON.stringify({
              policiesIds: selectedPolicies,
              truckingCompanyId: insuredId || '',
              fileParentId: '5A7BD49C-B7A8-4ABF-B713-31892FDE171A',
              naics: [{ Code: '', Name: '' }],
              Page: 'certificateFiller',
            }),
          }
        );
        if (cancelled) return;
        const json = await res.json();
        const arr: AcordFieldItem[] = Array.isArray(json.Data) ? json.Data : [];
        if (arr.length > 0) prefillFromApi(arr);

        // Tracker #257: a policy with no property section (an Auto line, a Builders Risk line)
        // comes back with no insurer at all, so fill the insurer slots from the selected
        // policies' carrier records instead, the way ACORD 27/28 do. Each distinct carrier
        // takes the next letter; the NAIC comes from the carrier record.
        const hasInsurerFromCoverages = ['A', 'B', 'C', 'D', 'E', 'F'].some(l => getInsurers(arr)[l].name);
        if (!hasInsurerFromCoverages) {
          const detailRes = await fetch(`${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/policy-details-byid`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', NCToken: ncToken, Authorization: `Bearer ${igToken}` },
            body: JSON.stringify({ databaseIds: selectedPolicies }),
          });
          if (cancelled) return;
          if (detailRes.ok) {
            const detailJson = await detailRes.json();
            const details: any[] = Array.isArray(detailJson.value) ? detailJson.value : [];
            const carriers: Array<{ name: string; naic: string }> = [];
            details.forEach(d => {
              const name = (d?.carrierName || '').trim();
              if (name && !carriers.some(c => c.name === name)) carriers.push({ name, naic: (d?.carrierNAIC || '').trim() });
            });
            const nameSetters = [setInsurerA, setInsurerB, setInsurerC, setInsurerD, setInsurerE, setInsurerF];
            const naicSetters = [setInsurerANaic, setInsurerBNaic, setInsurerCNaic, setInsurerDNaic, setInsurerENaic, setInsurerFNaic];
            const idSetters = [setInsurerAId, setInsurerBId, setInsurerCId, setInsurerDId, setInsurerEId, setInsurerFId];
            carriers.slice(0, 6).forEach((c, i) => {
              const L = 'ABCDEF'[i];
              nameSetters[i](own(`Insurer${L}`, c.name));
              naicSetters[i](own(`Insurer${L}Naic`, c.naic));
              resolveCarrierIdByName(c.name).then(id => { if (id) idSetters[i](own(`Insurer${L}Id`, id)); });
            });
          }
        }

        // Runs whenever the policy selection changes (this branch is never reached on the
        // very first edit-mode load — that's handled by the XML-restore branch above).
        // Prefer the current first-selected policy's address; otherwise fall back to client.
        const policyAddress = pickPolicyAddress(getPolicyAddresses(arr), selectedPolicies[0]);
        if (policyAddress) {
          setInsuredAddress(policyAddress.AddressLine1 || '');
          setInsuredStreet(policyAddress.AddressLine2 || '');
          setInsuredCity(policyAddress.City || '');
          setInsuredState(policyAddress.State || '');
          setInsuredZip(policyAddress.ZipCode || '');
        } else if (client) {
          setInsuredAddress(client.addressLine1 || '');
          setInsuredStreet(client.addressLine2 || '');
          setInsuredCity(client.city || '');
          setInsuredState(client.state || '');
          setInsuredZip(client.zip || '');
        }
      } catch {
        // silently fail — user can still fill manually
      } finally {
        if (!cancelled) onLoadingChange?.(false);
      }
    };
    fetchCoverages();
    return () => { cancelled = true; onLoadingChange?.(false); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPolicies, insuredId]);

  // Prefill the producer/contact block from the first selected policy's agent. With no policy
  // the block is blank (Farmers, 9/22): clear it on deselect so a previous pick doesn't linger.
  // Edit mode keeps whatever the stored XML restored.
  React.useEffect(() => {
    if (!selectedPolicies || selectedPolicies.length === 0) {
      if (isEditMode.current) return;
      setContactName(''); setContactPhone(''); setContactFax(''); setContactEmail('');
      setProducerAddress(''); setProducerStreet(''); setProducerCity(''); setProducerState(''); setProducerZip('');
      setAcord101Agency('');
      return;
    }
    fetchFirstPolicyAgentContact(selectedPolicies).then(async ({ agentId, name, phone, fax, email }) => {
      if (name) setContactName(name);
      if (phone) setContactPhone(phone);
      if (fax) setContactFax(fax);
      if (email) setContactEmail(email);
      if (agentId && name) {
        const { npnNumber, addressLine1, city, state, zipCode } = await fetchAgentContact(agentId);
        setProducerAddress(npnNumber ? `${name} (${npnNumber})` : name);
        if (addressLine1) setProducerStreet(addressLine1);
        if (city) setProducerCity(city);
        if (state) setProducerState(state);
        if (zipCode) setProducerZip(zipCode);
        const agencyParts = [name, addressLine1, city, state, zipCode].filter(Boolean);
        setAcord101Agency(agencyParts.join(', '));
      }
    });
  }, [selectedPolicies]);


  // Refresh the remarks textarea from selected policies' AdditionalNames custom field on
  // every policy selection change — re-derived fully each time, not merged with prior content.
  // Disabled for now — prod team isn't ready for the auto-filled remark yet.
  React.useEffect(() => {
    if (isEditMode.current) return;
    // setAcord101AdditionalRemarks(acord101AdditionalNamedInsureds ? `Additional Named Insured(s): ${acord101AdditionalNamedInsureds}` : '');
  }, [acord101AdditionalNamedInsureds]);


  React.useImperativeHandle(ref, () => ({
    applyBuildingCoverages(returnObjects: Array<{ fieldName: string; value: string }>) {
      prefillPropertyFromBuildingCoverages(returnObjects);
    },
    getFormData: (): Acord24FormData => {
      // Synchronous fallback: if state ID is still null (async lookup pending),
      // read directly from the already-loaded dropdown cache
      const resolvedAId = insurerAId ?? lookupCarrierIdSync(insurerA);
      const resolvedBId = insurerBId ?? lookupCarrierIdSync(insurerB);
      const resolvedCId = insurerCId ?? lookupCarrierIdSync(insurerC);
      const resolvedDId = insurerDId ?? lookupCarrierIdSync(insurerD);
      const resolvedEId = insurerEId ?? lookupCarrierIdSync(insurerE);
      const resolvedFId = insurerFId ?? lookupCarrierIdSync(insurerF);
      return ({
      producerAddress, producerCity, producerStreet, producerState, producerZip,
      contactName, contactPhone, contactFax, contactEmail, producerCustomerId,
      insuredName, insuredAddress, insuredStreet, insuredCity, insuredState, insuredZip,
      insurerA, insurerB, insurerC, insurerD, insurerE, insurerF,
      insurerAId: resolvedAId, insurerBId: resolvedBId, insurerCId: resolvedCId,
      insurerDId: resolvedDId, insurerEId: resolvedEId, insurerFId: resolvedFId,
      insurerANaic, insurerBNaic, insurerCNaic, insurerDNaic, insurerENaic, insurerFNaic,
      certDate, certNumber, revisionNumber, locationProperty, descriptionOfProperty, specialConditions,
      propInsurerLtr, propChecked, propPolicyNumber, propEffDate, propExpDate,
      propBasic, propBroad, propSpecial, propEarthquake, propWind, propFlood,
      propDeductBuilding, propDeductContents,
      propEarthquakeDeduct, propWindDeduct, propFloodDeduct,
      propOther1, propOther1Text, propOther1Deduct, propOther2, propOther2Text, propOther2Deduct,
      propCovBuilding, propLimBuilding,
      propCovPersonalProp, propLimPersonalProp,
      propCovBusinessIncome, propLimBusinessIncome,
      propCovExtraExpense, propLimExtraExpense,
      propCovRentalValue, propLimRentalValue,
      propCovBlanketBuilding, propLimBlanketBuilding,
      propCovBlanketPersProp, propLimBlanketPersProp,
      propCovBlanketBldgPP, propLimBlanketBldgPP,
      propOtherCov1, propOtherCov1Desc, propOtherCov1Lim,
      propOtherCov2, propOtherCov2Desc, propOtherCov2Lim,
      inlandInsurerLtr, inlandChecked, inlandNamedPerils, inlandOtherPerils, inlandOtherPerilsText,
      inlandTypeOfPolicy, inlandPolicyNumber, inlandEffDate, inlandExpDate,
      inlandCov1, inlandCov1Desc, inlandCov1Lim,
      inlandCov2, inlandCov2Desc, inlandCov2Lim,
      inlandCov3, inlandCov3Desc, inlandCov3Lim,
      inlandCov4, inlandCov4Desc, inlandCov4Lim,
      crimeInsurerLtr, crimeChecked, crimeTypeOfPolicy, crimeCausesOfLoss, crimePolicyNumber, crimeEffDate, crimeExpDate,
      crimeCov1, crimeCov1Desc, crimeCov1Lim,
      crimeCov2, crimeCov2Desc, crimeCov2Lim,
      crimeCov3, crimeCov3Desc, crimeCov3Lim,
      boilerInsurerLtr, boilerChecked, boilerPolicyNumber, boilerEffDate, boilerExpDate,
      boilerCov1, boilerCov1Desc, boilerCov1Lim,
      boilerCov2, boilerCov2Desc, boilerCov2Lim,
      otherInsurerLtr, otherType, otherPolicyNumber, otherEffDate, otherExpDate,
      otherCov1, otherCov1Desc, otherCov1Lim,
      otherCov2, otherCov2Desc, otherCov2Lim,
      holderName, holderAddress, holderCity, holderStreet, holderState, holderZip,
      authRepSignature,
      showAcord101,
      acord101Agency, acord101NamedInsured, acord101AdditionalRemarks,
    });
    },
  }));

  return (
    <>
      <div className="masterCert__section">
        {/* ACORD HEADER */}
        <div className="masterCert__acordHeader">
          <img src={acordLogo} alt="ACORD" className="masterCert__acordLogo" />
          <div className="masterCert__acordTitleWrapper">
            <div className="masterCert__acordTitle">CERTIFICATE OF PROPERTY INSURANCE</div>
          </div>
          <div className="masterCert__dateWrapper">
            <div className="masterCert__dateLabel">DATE (MM/DD/YYYY)</div>
            <div className="masterCert__dateCell">
              <Calendar value={certDate} onChange={(e) => setCertDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
          </div>
        </div>

        {/* DISCLAIMER */}
        <div className="masterCert__disclaimer">
          This certificate is issued as a matter of information only and confers no rights upon the certificate holder. This certificate does not affirmatively or negatively amend, extend or alter the coverage afforded by the policies below. This certificate of insurance does not constitute a contract between the issuing insurer(s), authorized representative or producer, and the certificate holder.
        </div>
        <div className="masterCert__disclaimerImportant">
          If this certificate is being prepared for a party who has an insurable interest in the property, do not use this form. Use ACORD 27 or ACORD 28.
        </div>

        {/* PRODUCER & CONTACT / INSURED & INSURERS */}
        <div className="masterCert__twoColumnLayout">
          {/* LEFT COLUMN */}
          <div>
            {/* PRODUCER */}
            <ProducerSection
              address={producerAddress}
              onAddressChange={setProducerAddress}
              city={producerCity}
              onCityChange={setProducerCity}
              street={producerStreet}
              onStreetChange={setProducerStreet}
              state={producerState}
              onStateChange={setProducerState}
              zip={producerZip}
              onZipChange={setProducerZip}
              onProducerSelect={(p) => {
                const parts = [p.businessName, p.adressLine1, p.city, p.state, p.zip].filter(Boolean);
                setAcord101Agency(parts.join(', '));
              }}
              onAgentContact={(phone, fax, email) => { setContactPhone(phone); setContactFax(fax); setContactEmail(email); }}
            />

            {/* INSURED */}
            <div>
              <div className="masterCert__sectionHeader">INSURED</div>
              <InputText maxLength={100} value={insuredName} onChange={(e) => setInsuredName(e.target.value)} placeholder="Type insured name" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
              <InputText maxLength={100} value={insuredAddress} onChange={(e) => setInsuredAddress(e.target.value)} placeholder="Address" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
              <InputText maxLength={100} value={insuredStreet} onChange={(e) => setInsuredStreet(e.target.value)} placeholder="Address Line 1" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
              <div className="masterCert__addressGrid">
                <InputText maxLength={100} value={insuredCity} onChange={(e) => setInsuredCity(e.target.value)} placeholder="City" className="masterCert__input masterCert__inputFull" />
                <InputText maxLength={50} value={insuredState} onChange={(e) => setInsuredState(e.target.value)} placeholder="State" className="masterCert__input masterCert__inputFull" />
                <InputText maxLength={20} value={insuredZip} onChange={(e) => setInsuredZip(e.target.value)} placeholder="Zip" className="masterCert__input masterCert__inputFull" />
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div>
            {/* CONTACT INFO */}
            <div style={{ marginBottom: 16 }}>
              <div className="masterCert__contactGrid">
                <div>
                  <div className="masterCert__contactLabel">CONTACT NAME:</div>
                  <InputText maxLength={100} value={contactName} onChange={(e) => setContactName(e.target.value)} className="masterCert__input masterCert__inputFull" />
                </div>
                <div></div>
              </div>
              <div className="masterCert__contactGrid">
                <div>
                  <div className="masterCert__contactLabel">PHONE (A/C, No, Ext):</div>
                  <InputText value={formatUsPhone(contactPhone)} onChange={(e) => setContactPhone(e.target.value)} className="masterCert__input masterCert__inputFull" />
                </div>
                <div>
                  <div className="masterCert__contactLabel">FAX (A/C, No):</div>
                  <InputText value={formatUsPhone(contactFax)} onChange={(e) => setContactFax(e.target.value)} className="masterCert__input masterCert__inputFull" />
                </div>
              </div>
              <div style={{ marginBottom: 6 }}>
                <div className="masterCert__contactLabel">E-MAIL ADDRESS:</div>
                <InputText maxLength={100} value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className="masterCert__input masterCert__inputFull" />
              </div>
              <div>
                <div className="masterCert__contactLabel">PRODUCER CUSTOMER ID #:</div>
                <InputText maxLength={30} value={producerCustomerId} onChange={(e) => setProducerCustomerId(e.target.value)} className="masterCert__input masterCert__inputFull" />
              </div>
            </div>

            {/* INSURERS AFFORDING COVERAGE */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                <div className="masterCert__sectionHeader" style={{ marginBottom: 0 }}>INSURERS AFFORDING COVERAGE (and NAIC #)</div>
                <Button className="carrierDialog__secondaryBtn" label="Add New" icon="pi pi-plus" outlined size="small" style={{ flexShrink: 0, padding: '2px 10px', fontSize: '0.7rem', height: '28px' }} onClick={() => setShowCreateCarrier(true)} />
              </div>
              <div className="masterCert__insurersGrid">
                <div className="masterCert__insurerLabel">INSURER A:</div>
                <InsurerNaicField name={insurerA} id={insurerAId} naic={insurerANaic} onNameChange={setInsurerA} onIdChange={setInsurerAId} onNaicChange={setInsurerANaic} className="masterCert__input masterCert__inputFull" />
                <div className="masterCert__insurerLabel">INSURER B:</div>
                <InsurerNaicField name={insurerB} id={insurerBId} naic={insurerBNaic} onNameChange={setInsurerB} onIdChange={setInsurerBId} onNaicChange={setInsurerBNaic} className="masterCert__input masterCert__inputFull" />
                <div className="masterCert__insurerLabel">INSURER C:</div>
                <InsurerNaicField name={insurerC} id={insurerCId} naic={insurerCNaic} onNameChange={setInsurerC} onIdChange={setInsurerCId} onNaicChange={setInsurerCNaic} className="masterCert__input masterCert__inputFull" />
                <div className="masterCert__insurerLabel">INSURER D:</div>
                <InsurerNaicField name={insurerD} id={insurerDId} naic={insurerDNaic} onNameChange={setInsurerD} onIdChange={setInsurerDId} onNaicChange={setInsurerDNaic} className="masterCert__input masterCert__inputFull" />
                <div className="masterCert__insurerLabel">INSURER E:</div>
                <InsurerNaicField name={insurerE} id={insurerEId} naic={insurerENaic} onNameChange={setInsurerE} onIdChange={setInsurerEId} onNaicChange={setInsurerENaic} className="masterCert__input masterCert__inputFull" />
                <div className="masterCert__insurerLabel">INSURER F:</div>
                <InsurerNaicField name={insurerF} id={insurerFId} naic={insurerFNaic} onNameChange={setInsurerF} onIdChange={setInsurerFId} onNaicChange={setInsurerFNaic} className="masterCert__input masterCert__inputFull" />
              </div>
            </div>
          </div>
        </div>

        {/* ══════════ COVERAGES ══════════ */}
        <div className="masterCert__coveragesSection">
          <div className="masterCert__coveragesTitle">COVERAGES</div>
          <div className="masterCert__certNumberGrid">
            <div>
              <div className="masterCert__contactLabel">CERTIFICATE NUMBER:</div>
              <InputText maxLength={30} value={certNumber} onChange={(e) => setCertNumber(e.target.value)} className="masterCert__input masterCert__inputFull" />
            </div>
            <div>
              <div className="masterCert__contactLabel">REVISION NUMBER:</div>
              <InputText maxLength={30} value={revisionNumber} onChange={(e) => setRevisionNumber(e.target.value)} className="masterCert__input masterCert__inputFull" />
            </div>
          </div>

          <div className="masterCert__disclaimer" style={{ marginBottom: 12 }}>
            This is to certify that the policies of insurance listed below have been issued to the insured named above for the policy period indicated. Notwithstanding any requirement, term or condition of any contract or other document with respect to which this certificate may be issued or may pertain, the insurance afforded by the policies described herein is subject to all the terms, exclusions and conditions of such policies. Limits shown may have been reduced by paid claims.
          </div>

          {/* Location of Premises / Description of Property */}
          <div className="masterCert__descriptionSection">
            <div className="masterCert__descriptionTitle">LOCATION OF PREMISES / DESCRIPTION OF PROPERTY (Attach ACORD 101, Additional Remarks Schedule, if more space is required)</div>
{/* <Dropdown
              value={selectedPropertyId}
              onChange={(e) => {
                setSelectedPropertyId(e.value);
                const selected = propertyOptions.find(p => p.value === e.value);
                if (selected) {
                  const newEntry = `Address: ${selected.label}`;
                  setLocationProperty(prev => {
                    const lines = prev ? prev.split('\n') : [];
                    return lines.includes(newEntry) ? prev : (prev ? `${prev}\n${newEntry}` : newEntry);
                  });
                  setDescriptionOfProperty(prev => {
                    const lines = prev ? prev.split('\n') : [];
                    return lines.includes(newEntry) ? prev : (prev ? `${prev}\n${newEntry}` : newEntry);
                  });
                }
                if (e.value && (isV2016 || showAcord101)) {
                  const formNumber = 2416;
                  fetchBuildingCoverages(e.value, formNumber)
                    .then(results => {
                      const allReturnObjects = results.flatMap((r: any) =>
                        Array.isArray(r.returnObjects) ? r.returnObjects : []
                      );
                      if (allReturnObjects.length > 0) prefillPropertyFromBuildingCoverages(allReturnObjects);
                    })
                    .catch((err) => console.error('building-coverages error:', err));
                }
              }}
              options={propertyOptions}
              optionLabel="label"
              optionValue="value"
              placeholder="Type and/or choose locations"
              className="masterCert__input masterCert__inputFull masterCert__inputMbSm"
              filter
              emptyMessage="No properties found"
            /> */}
            <InputTextarea value={descriptionOfProperty} onChange={(e) => setDescriptionOfProperty(e.target.value)} rows={3} className="masterCert__descriptionTextarea" />
          </div>

          {/* Coverage Table Header */}
          <div className="masterCert__coverageHeader24">
            <div>INSR<br/>LTR</div>
            <div>TYPE OF INSURANCE</div>
            <div>POLICY NUMBER</div>
            <div className="masterCert__coverageHeaderCenter">POLICY EFFECTIVE<br/>DATE (MM/DD/YY)</div>
            <div className="masterCert__coverageHeaderCenter">POLICY EXPIRATION<br/>DATE (MM/DD/YY)</div>
            <div>COVERED PROPERTY</div>
            <div>LIMITS</div>
          </div>

          {/* ── PROPERTY ── */}
          <div className="masterCert__coverageRow24">
            <div className="masterCert__coverageInsr">
              <Dropdown value={propInsurerLtr} onChange={(e) => setPropInsurerLtr(e.value)} options={INSR_OPTIONS} placeholder="" className="masterCert__input" emptyMessage="No available options" />
            </div>
            <div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="propChecked" checked={propChecked} onChange={(e) => setPropChecked(e.checked ?? false)} />
                <label htmlFor="propChecked" className="masterCert__coverageType" style={{ marginLeft: 4 }}>PROPERTY</label>
              </div>
              {/* Two-column: CAUSES OF LOSS | DEDUCTIBLES */}
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '3px 8px', marginTop: 4 }}>
                <div className="masterCert__contactLabel">CAUSES OF LOSS</div>
                <div className="masterCert__contactLabel">DEDUCTIBLES</div>

                {/* Row header: BUILDING deductible sub-label sits in deductibles col */}
                <div className="masterCert__checkboxItem"><Checkbox inputId="propBasic" checked={propBasic} onChange={(e) => setPropBasic(e.checked ?? false)} /><label htmlFor="propBasic" className="masterCert__checkboxItemLabel">BASIC</label></div>
                <div>
                  <div style={{ fontSize: '0.65rem', color: '#777', marginBottom: 1 }}>BUILDING</div>
                  <InputText value={propDeductBuilding} onChange={(e) => setPropDeductBuilding(e.target.value)} className="masterCert__input masterCert__inputFull" />
                </div>

                <div className="masterCert__checkboxItem"><Checkbox inputId="propBroad" checked={propBroad} onChange={(e) => setPropBroad(e.checked ?? false)} /><label htmlFor="propBroad" className="masterCert__checkboxItemLabel">BROAD</label></div>
                <div />

                <div className="masterCert__checkboxItem"><Checkbox inputId="propSpecial" checked={propSpecial} onChange={(e) => setPropSpecial(e.checked ?? false)} /><label htmlFor="propSpecial" className="masterCert__checkboxItemLabel">SPECIAL</label></div>
                <div>
                  <div style={{ fontSize: '0.65rem', color: '#777', marginBottom: 1 }}>CONTENTS</div>
                  <InputText value={propDeductContents} onChange={(e) => setPropDeductContents(e.target.value)} className="masterCert__input masterCert__inputFull" />
                </div>

                <div className="masterCert__checkboxItem"><Checkbox inputId="propEarthquake" checked={propEarthquake} onChange={(e) => setPropEarthquake(e.checked ?? false)} /><label htmlFor="propEarthquake" className="masterCert__checkboxItemLabel">EARTHQUAKE</label></div>
                <InputText value={propEarthquakeDeduct} onChange={(e) => setPropEarthquakeDeduct(e.target.value)} className="masterCert__input masterCert__inputFull" />

                <div className="masterCert__checkboxItem"><Checkbox inputId="propWind" checked={propWind} onChange={(e) => setPropWind(e.checked ?? false)} /><label htmlFor="propWind" className="masterCert__checkboxItemLabel">WIND</label></div>
                <InputText value={propWindDeduct} onChange={(e) => setPropWindDeduct(e.target.value)} className="masterCert__input masterCert__inputFull" />

                <div className="masterCert__checkboxItem"><Checkbox inputId="propFlood" checked={propFlood} onChange={(e) => setPropFlood(e.checked ?? false)} /><label htmlFor="propFlood" className="masterCert__checkboxItemLabel">FLOOD</label></div>
                <InputText value={propFloodDeduct} onChange={(e) => setPropFloodDeduct(e.target.value)} className="masterCert__input masterCert__inputFull" />

                <div className="masterCert__checkboxItem" style={{ alignItems: 'center' }}><Checkbox inputId="propOther1" checked={propOther1} onChange={(e) => setPropOther1(e.checked ?? false)} /><InputText value={propOther1Text} onChange={(e) => setPropOther1Text(e.target.value)} className="masterCert__input" style={{ marginLeft: 4, flex: 1 }} /></div>
                <InputText value={propOther1Deduct} onChange={(e) => setPropOther1Deduct(e.target.value)} className="masterCert__input masterCert__inputFull" />

                <div className="masterCert__checkboxItem" style={{ alignItems: 'center' }}><Checkbox inputId="propOther2" checked={propOther2} onChange={(e) => setPropOther2(e.checked ?? false)} /><InputText value={propOther2Text} onChange={(e) => setPropOther2Text(e.target.value)} className="masterCert__input" style={{ marginLeft: 4, flex: 1 }} /></div>
                <InputText value={propOther2Deduct} onChange={(e) => setPropOther2Deduct(e.target.value)} className="masterCert__input masterCert__inputFull" />
              </div>
            </div>
            <InputText value={propPolicyNumber} onChange={(e) => setPropPolicyNumber(e.target.value)} placeholder={noPolicyPlaceholder} className="masterCert__input masterCert__inputFull" />
            <div className="masterCert__dateCell">
              <Calendar value={propEffDate} onChange={(e) => setPropEffDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
            <div className="masterCert__dateCell">
              <Calendar value={propExpDate} onChange={(e) => setPropExpDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
            {/* Covered Property + Limits (spans 2 columns) */}
            <div className="masterCert__coveredLimits">
              <div className="masterCert__checkboxItem"><Checkbox inputId="propCovBuilding" checked={propCovBuilding} onChange={(e) => setPropCovBuilding(e.checked ?? false)} /><label htmlFor="propCovBuilding" className="masterCert__checkboxItemLabel">BUILDING</label></div>
              <InputText value={propLimBuilding} onChange={(e) => setPropLimBuilding(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="propCovPP" checked={propCovPersonalProp} onChange={(e) => setPropCovPersonalProp(e.checked ?? false)} /><label htmlFor="propCovPP" className="masterCert__checkboxItemLabel">PERSONAL PROPERTY</label></div>
              <InputText value={propLimPersonalProp} onChange={(e) => setPropLimPersonalProp(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="propCovBI" checked={propCovBusinessIncome} onChange={(e) => setPropCovBusinessIncome(e.checked ?? false)} /><label htmlFor="propCovBI" className="masterCert__checkboxItemLabel">BUSINESS INCOME</label></div>
              <InputText value={propLimBusinessIncome} onChange={(e) => setPropLimBusinessIncome(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="propCovEE" checked={propCovExtraExpense} onChange={(e) => setPropCovExtraExpense(e.checked ?? false)} /><label htmlFor="propCovEE" className="masterCert__checkboxItemLabel">EXTRA EXPENSE</label></div>
              <InputText value={propLimExtraExpense} onChange={(e) => setPropLimExtraExpense(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="propCovRV" checked={propCovRentalValue} onChange={(e) => setPropCovRentalValue(e.checked ?? false)} /><label htmlFor="propCovRV" className="masterCert__checkboxItemLabel">RENTAL VALUE</label></div>
              <InputText value={propLimRentalValue} onChange={(e) => setPropLimRentalValue(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="propCovBB" checked={propCovBlanketBuilding} onChange={(e) => setPropCovBlanketBuilding(e.checked ?? false)} /><label htmlFor="propCovBB" className="masterCert__checkboxItemLabel">BLANKET BUILDING</label></div>
              <InputText value={propLimBlanketBuilding} onChange={(e) => setPropLimBlanketBuilding(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="propCovBPP" checked={propCovBlanketPersProp} onChange={(e) => setPropCovBlanketPersProp(e.checked ?? false)} /><label htmlFor="propCovBPP" className="masterCert__checkboxItemLabel">BLANKET PERS PROP</label></div>
              <InputText value={propLimBlanketPersProp} onChange={(e) => setPropLimBlanketPersProp(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="propCovBBPP" checked={propCovBlanketBldgPP} onChange={(e) => setPropCovBlanketBldgPP(e.checked ?? false)} /><label htmlFor="propCovBBPP" className="masterCert__checkboxItemLabel">BLANKET BLDG &amp; PP</label></div>
              <InputText value={propLimBlanketBldgPP} onChange={(e) => setPropLimBlanketBldgPP(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="propOtherCov1" checked={propOtherCov1} onChange={(e) => setPropOtherCov1(e.checked ?? false)} /><InputText value={propOtherCov1Desc} onChange={(e) => setPropOtherCov1Desc(e.target.value)} placeholder="Other" className="masterCert__input masterCert__checkboxItemLabel" /></div>
              <InputText value={propOtherCov1Lim} onChange={(e) => setPropOtherCov1Lim(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="propOtherCov2" checked={propOtherCov2} onChange={(e) => setPropOtherCov2(e.checked ?? false)} /><InputText value={propOtherCov2Desc} onChange={(e) => setPropOtherCov2Desc(e.target.value)} placeholder="Other" className="masterCert__input masterCert__checkboxItemLabel" /></div>
              <InputText value={propOtherCov2Lim} onChange={(e) => setPropOtherCov2Lim(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
            </div>
          </div>

          {/* ── INLAND MARINE ── */}
          <div className="masterCert__coverageRow24">
            <div className="masterCert__coverageInsr">
              <Dropdown value={inlandInsurerLtr} onChange={(e) => setInlandInsurerLtr(e.value)} options={INSR_OPTIONS} placeholder="" className="masterCert__input" emptyMessage="No available options" />
            </div>
            <div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="inlandChecked" checked={inlandChecked} onChange={(e) => setInlandChecked(e.checked ?? false)} />
                <label htmlFor="inlandChecked" className="masterCert__coverageType" style={{ marginLeft: 4 }}>INLAND MARINE</label>
              </div>
              <div className="masterCert__contactLabel" style={{ marginTop: 4 }}>CAUSES OF LOSS</div>
              <div className="masterCert__checkboxItem"><Checkbox inputId="inlandNamedPerils" checked={inlandNamedPerils} onChange={(e) => setInlandNamedPerils(e.checked ?? false)} /><label htmlFor="inlandNamedPerils" className="masterCert__checkboxItemLabel">NAMED PERILS</label></div>
              <div className="masterCert__checkboxItem"><Checkbox inputId="inlandOtherPerils" checked={inlandOtherPerils} onChange={(e) => setInlandOtherPerils(e.checked ?? false)} /><InputText value={inlandOtherPerilsText} onChange={(e) => setInlandOtherPerilsText(e.target.value)} placeholder="" className="masterCert__input masterCert__inputSm" /></div>
              <div style={{ fontSize: '0.75rem', color: '#333', marginTop: 6, marginBottom: 2 }}>TYPE OF POLICY</div>
              <InputText value={inlandTypeOfPolicy} onChange={(e) => setInlandTypeOfPolicy(e.target.value)} className="masterCert__input masterCert__inputFull" />
            </div>
            <InputText value={inlandPolicyNumber} onChange={(e) => setInlandPolicyNumber(e.target.value)} placeholder={noPolicyPlaceholder} className="masterCert__input masterCert__inputFull" />
            <div className="masterCert__dateCell">
              <Calendar value={inlandEffDate} onChange={(e) => setInlandEffDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
            <div className="masterCert__dateCell">
              <Calendar value={inlandExpDate} onChange={(e) => setInlandExpDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
            <div className="masterCert__coveredLimits">
              <div className="masterCert__checkboxItem"><Checkbox inputId="inlandCov1" checked={inlandCov1} onChange={(e) => setInlandCov1(e.checked ?? false)} /><InputText value={inlandCov1Desc} onChange={(e) => setInlandCov1Desc(e.target.value)} placeholder="Coverage" className="masterCert__input masterCert__checkboxItemLabel" /></div>
              <InputText value={inlandCov1Lim} onChange={(e) => setInlandCov1Lim(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="inlandCov2" checked={inlandCov2} onChange={(e) => setInlandCov2(e.checked ?? false)} /><InputText value={inlandCov2Desc} onChange={(e) => setInlandCov2Desc(e.target.value)} placeholder="Coverage" className="masterCert__input masterCert__checkboxItemLabel" /></div>
              <InputText value={inlandCov2Lim} onChange={(e) => setInlandCov2Lim(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="inlandCov3" checked={inlandCov3} onChange={(e) => setInlandCov3(e.checked ?? false)} /><InputText value={inlandCov3Desc} onChange={(e) => setInlandCov3Desc(e.target.value)} placeholder="Coverage" className="masterCert__input masterCert__checkboxItemLabel" /></div>
              <InputText value={inlandCov3Lim} onChange={(e) => setInlandCov3Lim(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="inlandCov4" checked={inlandCov4} onChange={(e) => setInlandCov4(e.checked ?? false)} /><InputText value={inlandCov4Desc} onChange={(e) => setInlandCov4Desc(e.target.value)} placeholder="Coverage" className="masterCert__input masterCert__checkboxItemLabel" /></div>
              <InputText value={inlandCov4Lim} onChange={(e) => setInlandCov4Lim(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
            </div>
          </div>

          {/* ── CRIME ── */}
          <div className="masterCert__coverageRow24">
            <div className="masterCert__coverageInsr">
              <Dropdown value={crimeInsurerLtr} onChange={(e) => setCrimeInsurerLtr(e.value)} options={INSR_OPTIONS} placeholder="" className="masterCert__input" emptyMessage="No available options" />
            </div>
            <div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="crimeChecked" checked={crimeChecked} onChange={(e) => setCrimeChecked(e.checked ?? false)} />
                <label htmlFor="crimeChecked" className="masterCert__coverageType" style={{ marginLeft: 4 }}>CRIME</label>
              </div>
              <div style={{ fontSize: '0.75rem', color: '#333', marginTop: 4, marginBottom: 2 }}>TYPE OF POLICY</div>
              <InputTextarea value={crimeTypeOfPolicy} onChange={(e) => setCrimeTypeOfPolicy(e.target.value)} rows={2} className="masterCert__input masterCert__inputFull" autoResize />
            </div>
            <InputText value={crimePolicyNumber} onChange={(e) => setCrimePolicyNumber(e.target.value)} placeholder={noPolicyPlaceholder} className="masterCert__input masterCert__inputFull" />
            <div className="masterCert__dateCell">
              <Calendar value={crimeEffDate} onChange={(e) => setCrimeEffDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
            <div className="masterCert__dateCell">
              <Calendar value={crimeExpDate} onChange={(e) => setCrimeExpDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
            <div className="masterCert__coveredLimits">
              <div className="masterCert__checkboxItem"><Checkbox inputId="crimeCov1" checked={crimeCov1} onChange={(e) => setCrimeCov1(e.checked ?? false)} /><InputText value={crimeCov1Desc} onChange={(e) => setCrimeCov1Desc(e.target.value)} placeholder="Coverage" className="masterCert__input masterCert__checkboxItemLabel" /></div>
              <InputText value={crimeCov1Lim} onChange={(e) => setCrimeCov1Lim(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="crimeCov2" checked={crimeCov2} onChange={(e) => setCrimeCov2(e.checked ?? false)} /><InputText value={crimeCov2Desc} onChange={(e) => setCrimeCov2Desc(e.target.value)} placeholder="Coverage" className="masterCert__input masterCert__checkboxItemLabel" /></div>
              <InputText value={crimeCov2Lim} onChange={(e) => setCrimeCov2Lim(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="crimeCov3" checked={crimeCov3} onChange={(e) => setCrimeCov3(e.checked ?? false)} /><InputText value={crimeCov3Desc} onChange={(e) => setCrimeCov3Desc(e.target.value)} placeholder="Coverage" className="masterCert__input masterCert__checkboxItemLabel" /></div>
              <InputText value={crimeCov3Lim} onChange={(e) => setCrimeCov3Lim(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
            </div>
          </div>

          {/* ── BOILER & MACHINERY / EQUIPMENT BREAKDOWN ── */}
          <div className="masterCert__coverageRow24">
            <div className="masterCert__coverageInsr">
              <Dropdown value={boilerInsurerLtr} onChange={(e) => setBoilerInsurerLtr(e.value)} options={INSR_OPTIONS} placeholder="" className="masterCert__input" emptyMessage="No available options" />
            </div>
            <div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="boilerChecked" checked={boilerChecked} onChange={(e) => setBoilerChecked(e.checked ?? false)} />
                <label htmlFor="boilerChecked" className="masterCert__coverageType" style={{ marginLeft: 4 }}>BOILER &amp; MACHINERY / EQUIPMENT BREAKDOWN</label>
              </div>
            </div>
            <InputText value={boilerPolicyNumber} onChange={(e) => setBoilerPolicyNumber(e.target.value)} placeholder={noPolicyPlaceholder} className="masterCert__input masterCert__inputFull" />
            <div className="masterCert__dateCell">
              <Calendar value={boilerEffDate} onChange={(e) => setBoilerEffDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
            <div className="masterCert__dateCell">
              <Calendar value={boilerExpDate} onChange={(e) => setBoilerExpDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
            <div className="masterCert__coveredLimits">
              <div className="masterCert__checkboxItem"><Checkbox inputId="boilerCov1" checked={boilerCov1} onChange={(e) => setBoilerCov1(e.checked ?? false)} /><InputText value={boilerCov1Desc} onChange={(e) => setBoilerCov1Desc(e.target.value)} placeholder="Coverage" className="masterCert__input masterCert__checkboxItemLabel" /></div>
              <InputText value={boilerCov1Lim} onChange={(e) => setBoilerCov1Lim(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="boilerCov2" checked={boilerCov2} onChange={(e) => setBoilerCov2(e.checked ?? false)} /><InputText value={boilerCov2Desc} onChange={(e) => setBoilerCov2Desc(e.target.value)} placeholder="Coverage" className="masterCert__input masterCert__checkboxItemLabel" /></div>
              <InputText value={boilerCov2Lim} onChange={(e) => setBoilerCov2Lim(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
            </div>
          </div>

          {/* ── OTHER ── */}
          <div className="masterCert__coverageRow24">
            <div className="masterCert__coverageInsr">
              <Dropdown value={otherInsurerLtr} onChange={(e) => setOtherInsurerLtr(e.value)} options={INSR_OPTIONS} placeholder="" className="masterCert__input" emptyMessage="No available options" />
            </div>
            <div>
              <div className="masterCert__coverageType">OTHER</div>
              <InputTextarea maxLength={100} value={otherType} onChange={(e) => setOtherType(e.target.value)} rows={2} className="masterCert__input masterCert__inputFull" autoResize />
            </div>
            <InputText maxLength={50} value={otherPolicyNumber} onChange={(e) => setOtherPolicyNumber(e.target.value)} placeholder={noPolicyPlaceholder} className="masterCert__input masterCert__inputFull" />
            <div className="masterCert__dateCell">
              <Calendar value={otherEffDate} onChange={(e) => setOtherEffDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
            <div className="masterCert__dateCell">
              <Calendar value={otherExpDate} onChange={(e) => setOtherExpDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
            <div className="masterCert__coveredLimits">
              <div className="masterCert__checkboxItem"><Checkbox inputId="otherCov1" checked={otherCov1} onChange={(e) => setOtherCov1(e.checked ?? false)} /><InputText value={otherCov1Desc} onChange={(e) => setOtherCov1Desc(e.target.value)} placeholder="Coverage" className="masterCert__input masterCert__checkboxItemLabel" /></div>
              <InputText value={otherCov1Lim} onChange={(e) => setOtherCov1Lim(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="otherCov2" checked={otherCov2} onChange={(e) => setOtherCov2(e.checked ?? false)} /><InputText value={otherCov2Desc} onChange={(e) => setOtherCov2Desc(e.target.value)} placeholder="Coverage" className="masterCert__input masterCert__checkboxItemLabel" /></div>
              <InputText value={otherCov2Lim} onChange={(e) => setOtherCov2Lim(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
            </div>
          </div>
        </div>

        {/* SPECIAL CONDITIONS / OTHER COVERAGES */}
        <div className="masterCert__descriptionSection">
          <div className="masterCert__descriptionTitle">SPECIAL CONDITIONS / OTHER COVERAGES (Attach ACORD 101, Additional Remarks Schedule, if more space is required)</div>
          <InputTextarea value={specialConditions} onChange={(e) => setSpecialConditions(e.target.value)} rows={4} className="masterCert__descriptionTextarea" />
        </div>

        {/* CERTIFICATE HOLDER & CANCELLATION */}
        <div className="masterCert__bottomRow">
          <div className="masterCert__certHolderBox" style={!holderEnabled ? { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f9fafb', minHeight: 140 } : {}}>
            {!holderEnabled ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                <div className="masterCert__sectionHeader" style={{ marginBottom: 0, color: '#9ca3af' }}>CERTIFICATE HOLDER</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '11px', color: '#9ca3af' }}>
                  <i className="pi pi-info-circle" style={{ fontSize: '11px' }} />
                  <span>Certificate holder can be added while sending certificate.</span>
                </div>
                <Button
                  label="Enable"
                  icon="pi pi-lock-open"
                  size="small"
                  outlined
                  style={{ height: '28px', fontSize: '11px', padding: '0 14px', borderRadius: '4px', fontWeight: 500 }}
                  onClick={() => setHolderEnabled(true)}
                />
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div className="masterCert__sectionHeader" style={{ marginBottom: 0 }}>CERTIFICATE HOLDER</div>
                  <Button
                    label="Disable"
                    icon="pi pi-lock"
                    size="small"
                    style={{ height: '22px', fontSize: '10px', padding: '0 8px', borderRadius: '4px', fontWeight: 500 }}
                    onClick={() => { setHolderEnabled(false); setHolderName(''); setHolderAddress(''); setHolderCity(''); setHolderStreet(''); setHolderState(''); setHolderZip(''); setHolderSearchQuery(''); }}
                  />
                </div>
                <div style={{ position: 'relative', width: '100%', marginBottom: 6 }}>
                  <i className="pi pi-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: 13, zIndex: 1, pointerEvents: 'none' }} />
                  <AutoComplete
                    value={holderSearchQuery}
                    suggestions={holderSuggestions}
                    completeMethod={(e) => { setHolderSearchQuery(e.query); fetchHolderSuggestions(e.query); }}
                    onChange={(e) => { if (typeof e.value === 'string') setHolderSearchQuery(e.value); }}
                    field="label"
                    placeholder="Search for a certificate holder"
                    style={{ width: '100%' }}
                    inputStyle={{ width: '100%', paddingLeft: 30 }}
                    itemTemplate={(item: any) => {
                      if (!item) return null;
                      const r = item.value || {};
                      const location = [r.city, r.stateName].filter(Boolean).join(', ');
                      const address = [r.addressLine1, location, r.zipCode].filter(Boolean).join(' · ');
                      const aiDetails: any[] = (r.additionalInsuredDetails ?? [])
                        .filter((ai: any) => ai.truckingCompanyId === insuredId);
                      const isAdditionalInsured = aiDetails.length > 0 && (
                        selectedPolicies.length === 0 ||
                        aiDetails.some((ai: any) =>
                          (ai.policies ?? []).map((p: any) => p.databaseId as string)
                            .some((pid: string) => selectedPolicies.includes(pid))
                        )
                      );
                      return (
                        <div className="custom-ac-item custom-ac-item--rich">
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div className="custom-ac-item__name">{item.label}</div>
                            {isAdditionalInsured && (
                              <span style={{ fontSize: 10, fontWeight: 600, color: '#0057A4', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 4, padding: '1px 6px', whiteSpace: 'nowrap', lineHeight: '16px' }}>
                                Additional Insured
                              </span>
                            )}
                          </div>
                          {address && <div className="custom-ac-item__address">{address}</div>}
                        </div>
                      );
                    }}
                    onSelect={(e) => {
                      const h = e.value.value;
                      setHolderName(h.name || '');
                      setHolderAddress(h.addressLine1 || '');
                      setHolderStreet(h.addressLine2 || '');
                      setHolderCity(h.city || '');
                      setHolderState(h.stateName || '');
                      setHolderZip(h.zipCode || '');
                      setHolderSearchQuery('');
                    }}
                  />
                </div>
                <InputText maxLength={100} value={holderName} onChange={(e) => setHolderName(e.target.value)} placeholder="Name" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
                <InputText maxLength={100} value={holderAddress} onChange={(e) => setHolderAddress(e.target.value)} placeholder="Address" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
                <InputText maxLength={100} value={holderStreet} onChange={(e) => setHolderStreet(e.target.value)} placeholder="Address Line 1" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
                <div className="masterCert__addressGrid">
                  <InputText maxLength={100} value={holderCity} onChange={(e) => setHolderCity(e.target.value)} placeholder="City" className="masterCert__input masterCert__inputFull" />
                  <InputText maxLength={50} value={holderState} onChange={(e) => setHolderState(e.target.value)} placeholder="State" className="masterCert__input masterCert__inputFull" />
                  <InputText maxLength={20} value={holderZip} onChange={(e) => setHolderZip(e.target.value)} placeholder="Zip" className="masterCert__input masterCert__inputFull" />
                </div>
              </>
            )}
          </div>
          <div className="masterCert__cancellationBox">
            <div className="masterCert__sectionHeader">CANCELLATION</div>
            <div className="masterCert__cancellationText">
              Should any of the above described policies be cancelled before the expiration date thereof, notice will be delivered in accordance with the policy provisions.
            </div>
            <div className="masterCert__authRepLabel">AUTHORIZED REPRESENTATIVE</div>
            <div className="masterCert__authRepRow">
              <Dropdown value={authRepSignature} onChange={(e) => setAuthRepSignature(e.value)} options={signatureOptions} optionLabel="label" optionValue="value" placeholder="Select signature:" className="masterCert__input masterCert__authRepDropdown" emptyMessage="No available options" />
              <Button
                label="Create New"
                className="p-button-outlined carrierDialog__secondaryBtn"
                onClick={() => setShowAddSignature(true)}
              />
            </div>
              {signaturePreviewUrl && (
                <div style={{ marginTop: 8 }}>
                  <img src={signaturePreviewUrl} alt="Signature preview" style={{ maxHeight: 60, maxWidth: 280, objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 8px', background: '#fff' }} />
                </div>
              )}
          </div>
        </div>

        {/* ACORD Footer */}
        <div className="masterCert__acordFooter">
          <div>{isV2016 ? 'ACORD 24 (2016/03)' : 'ACORD 24 (2009/09)'}</div>
          <div>&copy; {isV2016 ? '1995-2015' : '1995-2009'} ACORD CORPORATION. All rights reserved.</div>
        </div>

        {/* ── ACORD 101 - Additional Remarks Schedule ── */}
        {showAcord101 && (
          <div className="masterCert__acord101">
            <div className="masterCert__acordHeader" style={{ marginTop: 24 }}>
              <img src={acordLogo} alt="ACORD" className="masterCert__acordLogo" />
              <div className="masterCert__acordTitleWrapper">
                <div className="masterCert__acordTitle">ADDITIONAL REMARKS SCHEDULE</div>
              </div>
            </div>
            <div className="masterCert__twoColumnLayout">
              <div>
                <div className="masterCert__sectionHeader">AGENCY</div>
                <InputTextarea maxLength={250} value={acord101Agency} onChange={(e) => setAcord101Agency(e.target.value)} rows={3} className="masterCert__descriptionTextarea" placeholder="Agency name and address" />
              </div>
              <div>
                <div className="masterCert__sectionHeader">NAMED INSURED</div>
                <InputTextarea maxLength={100} value={acord101NamedInsured} onChange={(e) => setAcord101NamedInsured(e.target.value)} rows={3} className="masterCert__descriptionTextarea" placeholder="Named insured" />
              </div>
            </div>
            <div className="masterCert__descriptionSection">
              <div className="masterCert__descriptionTitle">ADDITIONAL REMARKS</div>
              <InputTextarea maxLength={2000} value={acord101AdditionalRemarks} onChange={(e) => setAcord101AdditionalRemarks(e.target.value)} rows={8} className="masterCert__descriptionTextarea" placeholder="Enter additional remarks..." />
            </div>
            <div className="masterCert__acordFooter">
              <div>ACORD 101 (2008/01)</div>
              <div>&copy; 2008 ACORD CORPORATION. All rights reserved.</div>
            </div>
          </div>
        )}
      </div>

      <CreateNewCarriers open={showCreateCarrier} onClose={() => setShowCreateCarrier(false)} />
      <AddNewSignature open={showAddSignature} onClose={() => setShowAddSignature(false)} onSaved={refetchSignatures} />
    </>
  );
});

export default Acord24Form;
